import { ShotListModalParams } from './../shot-list-modal/shot-list-modal.component';
import {
  Component,
  computed,
  ElementRef,
  HostListener,
  Input,
  QueryList,
  ViewChild,
  ViewChildren,
  effect,
  inject,
  DestroyRef,
  signal,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { EMPTY, forkJoin, Observable, of, zip, Subject } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  switchMap,
  tap,
  debounceTime,
} from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { IndividualConfig, ToastrService } from 'ngx-toastr';

import {
  ScorecardResponse,
  Hole,
  TeeBox,
  ApiValidationError,
  ScoreResponse,
  Group,
  GroupSlot,
  Player,
  HoleYardage,
  TournamentCourse,
} from '../../models';

import {
  CourseService,
  GroupService,
  PlayerService,
  ScorecardService,
  TournamentService,
  AlertService,
} from '../../services';
import {
  ScoringRealtimeService,
  ScorecardActivityData,
} from '../../services/signalr/scoring-realtime.service';

import { AutoFocusNextDirective } from '../../directives/auto-focus-next/auto-focus-next.directive';
import { SelectOnFocusDirective } from '../../directives/select-on-focus/select-on-focus.directive';

import { ScorecardVerifyComponent } from '../scorecard-verify/scorecard-verify.component';
import { ScorecardLegendComponent } from '../scorecard-legend/scorecard-legend.component';
import { ConfigService } from '../../services/config/config.service';
import { ScorecardCalculationService } from '../../services/scorecard/calculation/scorecard-calculation.service';
import { ScorecardUtilitiesService } from '../../services/scorecard/utilities/scorecard-utilities.service';
import { ScoreBufferService } from '../../services/scorecard/buffer/score-buffer.service';
import { ShotListModalComponent } from '../shot-list-modal/shot-list-modal.component';
import { AlertContainerComponent } from '../shared/alert-container/alert-container.component';
import { ScorecardRefreshService } from '../../services/shared/alert/scorecard-refresh.service';
import {
  PlayerSearchModalComponent,
  SearchPlayer,
} from './../player-search-modal/player-search-modal.component';
import { LoggingService } from '../../services/shared/logging/logging.service';

export const SCORECARD_CONSTANTS = {
  HOLES_PER_ROUND: 18,
  HOLES_PER_SIDE: 9,
} as const;

export type ScorecardMode = 'scores' | 'stats' | 'verify';
export type ScoringType = 'group' | 'scorecard';
export type VerifyScoreMode = 'edit' | 'view';
export type VerifyScoreType = 'OUT' | 'IN' | 'RND' | 'TOTAL';

export interface ScoreInput {
  rowId: string;
  holeNumber: number;
}

export interface VerifyScore {
  holeNumber: number;
  score: number | null;
  valid?: boolean;
}

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  action: () => void;
  condition?: () => boolean;
}

@Component({
  selector: 'app-scorecard',
  imports: [
    CommonModule,
    FormsModule,
    AutoFocusNextDirective,
    SelectOnFocusDirective,
    ScorecardVerifyComponent,
    ScorecardLegendComponent,
    ShotListModalComponent,
    PlayerSearchModalComponent,
    AlertContainerComponent,
  ],
  templateUrl: './scorecard.component.html',
  styleUrls: ['./scorecard.component.scss'],
})
export class ScorecardComponent {
  @ViewChild(ShotListModalComponent) shotListModal!: ShotListModalComponent;
  @ViewChild(PlayerSearchModalComponent)
  playerSearchModal!: PlayerSearchModalComponent;

  showLegend = false;

  private readonly INITIAL_VERIFY_MODE: VerifyScoreMode = 'view';
  private readonly INITIAL_SCORING_TYPE: ScoringType = 'group';
  private readonly INITIAL_SCORECARD_MODE: ScorecardMode = 'scores';

  private _scorecardMode: ScorecardMode = this.INITIAL_SCORECARD_MODE;

  @ViewChildren('scoreInput') scoreInputs!: QueryList<ElementRef>;
  activeScoreInput: { rowId: string; holeNumber: number } | null = null;

  showAllScores = true; // default to showAllScores
  @Input()
  hideOfficialScorecards = false;

  // Add computed signals for scorecard counts and percentages
  readonly officialScorecardsCount = computed(
    () => this.scorecardService.scorecardCounts().official || 0
  );
  readonly unofficialScorecardsCount = computed(
    () => this.scorecardService.scorecardCounts().unofficial || 0
  );

  // Add computed signals for the percentages to use in the template
  readonly officialPercentage = computed(() => {
    const official = this.officialScorecardsCount();
    const unofficial = this.unofficialScorecardsCount();
    const total = official + unofficial;
    return total > 0 ? (official / total) * 100 : 0;
  });

  readonly unofficialPercentage = computed(() => {
    const official = this.officialScorecardsCount();
    const unofficial = this.unofficialScorecardsCount();
    const total = official + unofficial;
    return total > 0 ? (unofficial / total) * 100 : 0;
  });

  @Input()
  get scorecardMode(): ScorecardMode {
    return this._scorecardMode;
  }
  set scorecardMode(value: ScorecardMode) {
    if (!['scores', 'stats', 'verify'].includes(value)) {
      throw new Error('Invalid scorecard mode');
    }
    this._scorecardMode = value;
  }

  private _scoringType: ScoringType = this.INITIAL_SCORING_TYPE;
  @Input()
  get scoringType(): ScoringType {
    return this._scoringType;
  }
  set scoringType(value: ScoringType) {
    if (value !== 'group' && value !== 'scorecard') {
      throw new Error('Invalid scoring type');
    }
    this._scoringType = value;
  }

  queryRoundId: string | null = null;
  queryCourseId: string | null = null;
  queryGroupId: string | null = null;
  queryPlayerId: string | null = null;

  readonly selectedTournament = this.tournamentService.selectedTournamentSignal;
  readonly selectedRound = this.tournamentService.selectedRoundSignal;

  // Define signals for reactive state management
  readonly selectedCourse = signal<TournamentCourse | undefined>(undefined);
  readonly selectedGroupSignal = signal<Group | undefined>(undefined);
  readonly queryGroupIdSignal = signal<string | null>(null);
  readonly queryPlayerIdSignal = signal<string | null>(null);
  readonly selectedPlayerSignal = signal<Player | undefined>(undefined);

  // Legacy properties for transition to signals
  selectedGroup: Group | undefined;

  // Computed property to easily access the current course value
  get currentCourse(): TournamentCourse | undefined {
    return this.selectedCourse();
  }

  // Shorthand for internal component usage
  get groupNumber(): number | undefined {
    return this.selectedGroupSignal()?.number;
  }

  private _selectedPlayer: Player | undefined;
  get selectedPlayer(): Player | undefined {
    return this._selectedPlayer;
  }
  set selectedPlayer(value: Player | undefined) {
    this._selectedPlayer = value;
    // Keep signal in sync with legacy property
    this.selectedPlayerSignal.set(value);
    this.loggingService.debug(
      'ScoreCard',
      `Set selectedPlayer (legacy): ${value?.playFirstName} ${value?.playLastName}`
    );
  }

  onSearchPlayerSelected(player: SearchPlayer): void {
    const group = this.groups.find((g) => g.groupId === player.slot?.groupId);

    this.selectGroup(group!);

    if (!this.isScoresMode()) {
      setTimeout(() => {
        this.selectPlayer(player as Player);
      }, 150);
    }
  }

  get selectedPlayerCaption(): string {
    const player = this.selectedPlayerSignal();
    return player ? `${player.playLastName}, ${player.playFirstName}` : '';
  }

  teebox: TeeBox | undefined;
  groups: Group[] = [];
  players: Player[] = [];
  groupSlots: GroupSlot[] = [];

  holes: Hole[] = [];
  holeYardages: HoleYardage[] = [];
  holesOUT: Hole[] = [];
  holesIN: Hole[] = [];

  validOUT = false;
  validIN = false;

  get timestampVerified(): string | undefined {
    return this.getFilteredScorecard()?.verifiedOnUtc
      ? `${this.getFilteredScorecard()?.verifiedOnUtc}Z`
      : undefined;
  }

  get timestampOfficial(): string | undefined {
    return this.getFilteredScorecard()?.officialOnUtc
      ? `${this.getFilteredScorecard()?.officialOnUtc}Z`
      : undefined;
  }

  verifyScoreMode: VerifyScoreMode = 'view';
  verifyScoresOUT: VerifyScore[] = [];
  verifyScoresIN: VerifyScore[] = [];

  scores: { [scorecardId: string]: ScoreResponse[] } = {};
  scoresOUT: { [scorecardId: string]: ScoreResponse[] } = {};
  scoresIN: { [scorecardId: string]: ScoreResponse[] } = {};

  scorecards: ScorecardResponse[] = [];
  get filteredScorecards(): ScorecardResponse[] {
    // Use signal instead of legacy property for more reactive filtering
    const player = this.selectedPlayerSignal();
    if (player) {
      return this.scorecards.filter(
        (card) => card.playerId === player.playerId
      );
    }
    return this.isScoresMode() ? this.scorecards : [];
  }

  getFilteredScorecard(): ScorecardResponse | undefined {
    if (this.filteredScorecards.length === 1) {
      return this.filteredScorecards[0];
    }
    return undefined;
  }

  hasScoreValidationError: { [key: string]: boolean } = {};

  // Track scores that are currently being processed to prevent focus advancement on errors
  private scoresBeingProcessed = new Set<string>();

  // Track local score operations to conditionally prevent showing notifications for locally entered scores
  // (only used when notifications.suppressLocalOperations is enabled in config)
  // Changed to track multiple operations per scorecard instead of just one timestamp
  private localScoreOperations = new Map<string, number[]>();
  private cleanupInterval?: number;

  // Rate limiting for alerts
  private alertRateLimit = new Map<string, number>();
  private readonly ALERT_RATE_LIMIT_MS = 3000; // Minimum 3 seconds between alerts for same scorecard
  private readonly MAX_ALERTS_PER_MINUTE = 20; // Maximum alerts per minute
  private alertTimestamps: number[] = [];

  private cleanupExpiredOperations(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    // Use configurable cleanup window (default 5 seconds, which is 2.5x the suppression window)
    const suppressionWindowMs =
      this.configService.config?.notifications?.suppressionWindowMs ?? 2000;
    const cleanupWindowMs = suppressionWindowMs * 2.5; // Keep for 2.5x the suppression window

    for (const [
      scorecardId,
      timestamps,
    ] of this.localScoreOperations.entries()) {
      // Filter out expired timestamps
      const validTimestamps = timestamps.filter(
        (timestamp) => now - timestamp <= cleanupWindowMs
      );

      if (validTimestamps.length === 0) {
        // No valid timestamps left, remove the entire entry
        expiredKeys.push(scorecardId);
      } else if (validTimestamps.length < timestamps.length) {
        // Some timestamps expired, update with only valid ones
        this.localScoreOperations.set(scorecardId, validTimestamps);
      }
    }

    // Clean up entries with no valid timestamps
    expiredKeys.forEach((key) => this.localScoreOperations.delete(key));

    // Log cleanup if any entries were removed
    if (expiredKeys.length > 0) {
      this.loggingService.debug(
        'ScoreCard',
        `Cleaned up ${expiredKeys.length} scorecard entries with expired local operations`
      );
    }
  }

  private startPeriodicCleanup(): void {
    // Only start cleanup if local operation suppression is enabled
    const suppressLocalOperations =
      this.configService.config?.notifications?.suppressLocalOperations ??
      false;

    if (suppressLocalOperations) {
      // Clean up expired operations at configurable intervals (default every 10 seconds)
      const suppressionWindowMs =
        this.configService.config?.notifications?.suppressionWindowMs ?? 2000;
      const cleanupIntervalMs = Math.max(suppressionWindowMs * 5, 10000); // At least 5x suppression window, minimum 10 seconds

      this.cleanupInterval = window.setInterval(() => {
        this.cleanupExpiredOperations();
      }, cleanupIntervalMs);

      this.loggingService.debug(
        'ScoreCard',
        `Started periodic cleanup with interval: ${cleanupIntervalMs} ms`
      );
    } else {
      this.loggingService.debug(
        'ScoreCard',
        'Local operation suppression disabled, skipping periodic cleanup'
      );
    }
  }

  private stopPeriodicCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = undefined;
    }
  }

  private isAlertRateLimited(scorecardId: string): boolean {
    const now = Date.now();

    // Check per-scorecard rate limit
    const lastAlertTime = this.alertRateLimit.get(scorecardId);
    if (lastAlertTime && now - lastAlertTime < this.ALERT_RATE_LIMIT_MS) {
      this.loggingService.debug(
        'ScoreCard',
        `Alert rate limited for scorecard ${scorecardId}`
      );
      return true;
    }

    // Check global rate limit (alerts per minute)
    const oneMinuteAgo = now - 60000;
    this.alertTimestamps = this.alertTimestamps.filter(
      (timestamp) => timestamp > oneMinuteAgo
    );

    if (this.alertTimestamps.length >= this.MAX_ALERTS_PER_MINUTE) {
      this.loggingService.debug(
        'ScoreCard',
        `Global alert rate limit reached (${this.MAX_ALERTS_PER_MINUTE} alerts per minute)`
      );
      return true;
    }

    return false;
  }

  private recordAlert(scorecardId: string): void {
    const now = Date.now();
    this.alertRateLimit.set(scorecardId, now);
    this.alertTimestamps.push(now);
  }

  get groupTitle(): string {
    const group = this.selectedGroupSignal();
    if (!group) return '';

    const time = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(group.startTime!));

    // Use the signal instead of the legacy property
    const player = this.selectedPlayerSignal();
    const selectedPlayerName = player
      ? `${player.playLastName}, ${player.playFirstName} - `
      : '';

    return `${selectedPlayerName}Group ${group.number} - Tee ${group.startTee} - ${time}`;
  }

  getNavTitle(): string {
    if (this.isStatsMode()) return 'Stats';
    if (this.isScoresMode()) return 'Scores';
    if (this.isVerifyMode()) return 'Verify';
    return '';
  }

  private scoreChangeSubject = new Subject<{
    score: ScoreResponse;
    rowId: string;
    holeNumber: number;
  }>();

  private destroyRef = inject(DestroyRef);

  constructor(
    readonly route: ActivatedRoute,
    readonly router: Router,
    readonly tournamentService: TournamentService,
    readonly courseService: CourseService,
    readonly groupService: GroupService,
    readonly playerService: PlayerService,
    readonly scorecardService: ScorecardService,
    readonly scoreBufferService: ScoreBufferService,
    readonly toasterService: ToastrService,
    readonly alertService: AlertService,
    readonly configService: ConfigService,
    readonly calculationService: ScorecardCalculationService,
    readonly utilitiesService: ScorecardUtilitiesService,
    readonly scoringRealtimeService: ScoringRealtimeService,
    private readonly refreshService: ScorecardRefreshService,
    private readonly loggingService: LoggingService
  ) {
    // Effect: Load scorecard counts when tournament or round changes
    const scorecardCountsEffect = effect(() => {
      this.loggingService.debug(
        'ScoreCard',
        'Effect: Loading scorecard counts'
      );
      const tournament = this.selectedTournament();
      const round = this.selectedRound();

      this.loggingService.debug(
        'ScoreCard',
        'Scorecard counts Effect - Tournament:',
        tournament?.name
      );
      this.loggingService.debug(
        'ScoreCard',
        'Scorecard counts Effect - Round:',
        round ? `Round ${round.number}` : 'undefined'
      );

      if (tournament && round) {
        this.loggingService.debug(
          'ScoreCard',
          'Loading scorecard counts for round:',
          round.roundId
        );
        this.refreshRoundScorecardCounts();
      } else {
        this.loggingService.debug(
          'ScoreCard',
          'Clearing scorecard counts (no tournament or round)'
        );
        // Note: We can't directly reset the private signal, but it will be updated
        // when getScorecardCounts is called with appropriate data
      }
    });

    // Effect: fetch courses when selectedTournament changes
    const coursesEffect = effect(() => {
      this.loggingService.debug(
        'ScoreCard',
        'Effect: Fetching courses for tournament'
      );
      const tournament = this.selectedTournament();

      // Clear courses and selectedCourse if tournament is undefined
      if (!tournament || !tournament.tournamentId) {
        this.loggingService.debug(
          'ScoreCard',
          'No tournament selected, clearing courses and selectedCourse'
        );
        this.courses = [];
        this.selectedCourse.set(undefined);
        return;
      }

      this.loggingService.debug(
        'ScoreCard',
        'Fetching courses for tournament:',
        tournament.name
      );
      this.tournamentService
        .getTournamentCoursesByTournamentId(tournament.tournamentId)
        .subscribe({
          next: (courses: TournamentCourse[]) => {
            this.loggingService.debug(
              'ScoreCard',
              `Received ${courses.length} courses for tournament ${tournament.name}`
            );
            this.courses = courses;

            // Check if we should use the queryCourseId
            if (this.queryCourseId) {
              this.loggingService.debug(
                'ScoreCard',
                'Looking for course by queryCourseId:',
                this.queryCourseId
              );
              const queryCourse = courses.find(
                (c) => c.courseId === this.queryCourseId
              );
              if (queryCourse) {
                this.loggingService.debug(
                  'ScoreCard',
                  'Found course by queryCourseId:',
                  queryCourse.courseName
                );
                this.selectedCourse.set(queryCourse);
                return;
              }
            }

            // If no selectedCourse or selectedCourse not in courses, select the first course
            const currentCourse = this.selectedCourse();
            if (
              !currentCourse ||
              !courses.some((c) => c.courseId === currentCourse?.courseId)
            ) {
              if (courses.length > 0) {
                this.loggingService.debug(
                  'ScoreCard',
                  'Setting first course as selected:',
                  courses[0].courseName
                );
                this.selectedCourse.set(courses[0]);
              } else {
                this.loggingService.debug(
                  'ScoreCard',
                  'No courses available, clearing selectedCourse'
                );
                this.selectedCourse.set(undefined);
              }
            }
          },
          error: (err: any) => {
            this.loggingService.error(
              'ScoreCard',
              'Failed to fetch courses for tournament',
              err
            );
            this.courses = [];
            this.selectedCourse.set(undefined);
          },
        });
    });

    // Effect: fetch groups when selectedTournament, selectedRound, or selectedCourse changes
    const groupsEffect = effect(() => {
      this.loggingService.debug(
        'ScoreCard',
        'Effect: Fetching groups for tournament/round/course'
      );
      const tournament = this.selectedTournament();
      const round = this.selectedRound();
      const course = this.selectedCourse(); // Get the course from the signal

      this.loggingService.debug(
        'ScoreCard',
        'Group Effect - Tournament:',
        tournament?.name
      );
      this.loggingService.debug(
        'ScoreCard',
        'Group Effect - Round:',
        round ? `Round ${round.number}` : 'undefined'
      );
      this.loggingService.debug(
        'ScoreCard',
        'Group Effect - Course:',
        course?.courseName
      );

      // Clear groups if any required data is missing
      if (!tournament || !round || !course) {
        this.loggingService.debug(
          'ScoreCard',
          'Missing required data for group fetch, clearing groups'
        );
        this.groups = [];
        this.groupSlots = [];
        this.selectedGroup = undefined;
        return;
      }

      this.loggingService.debug(
        'ScoreCard',
        `All conditions met, fetching groups with: tournamentId=${tournament.tournamentId}, roundId=${round.roundId}, courseId=${course.courseId}`
      );

      // Add debug pause to ensure previous operations are complete
      setTimeout(() => {
        // Force clean group service cache to avoid using stale data
        this.groupService
          .getByTournamentRoundCourse(
            tournament.tournamentId!,
            round.roundId!,
            course.courseId!
          )
          .subscribe({
            next: (groups: Group[]) => {
              this.loggingService.debug(
                'ScoreCard',
                `Received ${groups.length} groups from API`
              );

              if (groups.length === 0) {
                this.loggingService.debug(
                  'ScoreCard',
                  'No groups returned from API - check tournament/round/course IDs'
                );
              }

              // Reset selected group (both signal and legacy property)
              this.selectedGroup = undefined;
              this.selectedGroupSignal.set(undefined);

              if (this.hideOfficialScorecards) {
                this.groups = groups.filter(
                  (g) => g.groupNotOfficialScorecardCount! > 0
                );
                this.loggingService.debug(
                  'ScoreCard',
                  `Filtered to ${this.groups.length} unofficial groups`
                );
              } else {
                this.groups = groups;
              }

              if (this.queryGroupId) {
                this.loggingService.debug(
                  'ScoreCard',
                  `Looking for specific group by queryGroupId: ${this.queryGroupId}`
                );
                const foundGroup = this.groups.find(
                  (g) => g.groupId === this.queryGroupId
                );

                // Update both the signal and legacy property
                this.selectedGroup = foundGroup;
                this.selectedGroupSignal.set(foundGroup);

                this.loggingService.debug(
                  'ScoreCard',
                  `Selected group by ID: ${
                    this.selectedGroup?.number || 'not found'
                  }`
                );
              } else if (this.groups.length > 0) {
                // Update both the signal and legacy property
                this.selectedGroup = this.groups[0];
                this.selectedGroupSignal.set(this.groups[0]);

                this.loggingService.debug(
                  'ScoreCard',
                  `Selected first group: ${this.selectedGroup?.number}`
                );
              } else {
                this.loggingService.debug(
                  'ScoreCard',
                  'No groups available to select'
                );
              }

              // Set groupSlots from the selected group
              this.groupSlots = this.selectedGroup?.slots || [];
              this.loggingService.debug(
                'ScoreCard',
                `Set ${this.groupSlots.length} group slots from selected group`
              );

              // Note: SignalR subscription moved to after scorecards are loaded
            },
            error: (err: any) => {
              this.loggingService.error(
                'ScoreCard',
                'Failed to fetch groups for tournament/round/course',
                err
              );
              this.groups = [];
              this.groupSlots = [];
              this.selectedGroup = undefined;
            },
          });
      }, 100); // Small delay to ensure course assignment is complete
    });

    // Effect: only call initializeData when all signals are set
    const initDataEffect = effect(() => {
      this.loggingService.debug(
        'ScoreCard',
        'Effect: Initializing data based on tournament/round/course'
      );
      const tournament = this.selectedTournament();
      const round = this.selectedRound();
      const course = this.selectedCourse(); // Get the course value from the signal

      this.loggingService.debug(
        'ScoreCard',
        `initDataEffect - Tournament: ${tournament?.name}`
      );
      this.loggingService.debug(
        'ScoreCard',
        `initDataEffect - Round: ${
          round ? `Round ${round.number}` : 'undefined'
        }`
      );
      this.loggingService.debug(
        'ScoreCard',
        `initDataEffect - Selected Course: ${course?.courseName}`
      );

      // We now require course to be set as well before initializing
      if (tournament && round && course) {
        this.loggingService.debug(
          'ScoreCard',
          'initDataEffect: ALL requirements met (tournament, round, AND course), calling initializeData()'
        );

        // Add a small delay to ensure all signals have propagated
        setTimeout(() => {
          this.initializeData();
        }, 200);
      } else {
        this.loggingService.debug(
          'ScoreCard',
          'initDataEffect: Missing required data (tournament, round, or course), not initializing'
        );
        if (!tournament)
          this.loggingService.debug('ScoreCard', 'Missing: tournament');
        if (!round) this.loggingService.debug('ScoreCard', 'Missing: round');
        if (!course) this.loggingService.debug('ScoreCard', 'Missing: course');
      }
    });

    // Effect: Update the selected group when the query param changes
    const queryGroupEffect = effect(() => {
      const queryGroupId = this.queryGroupIdSignal();
      this.loggingService.debug(
        'ScoreCard',
        `Effect: Query group ID changed: ${queryGroupId}`
      );

      if (queryGroupId && this.groups.length > 0) {
        this.loggingService.debug(
          'ScoreCard',
          `Looking for group with ID: ${queryGroupId}`
        );
        const group = this.groups.find((g) => g.groupId === queryGroupId);

        if (group) {
          this.loggingService.debug(
            'ScoreCard',
            `Found group by ID, setting as selected group: ${group.number}`
          );

          // Update both the signal and legacy property
          this.selectedGroupSignal.set(group);
          this.selectedGroup = group;

          // Set group slots immediately to improve UI responsiveness
          this.groupSlots = group.slots || [];
          this.loggingService.debug(
            'ScoreCard',
            `Set ${this.groupSlots.length} group slots from selected group`
          );

          // After group selection, fetch scorecards and scores
          this.fetchScorecards()
            .pipe(switchMap(() => this.fetchScores()))
            .subscribe({
              next: () => {
                this.loggingService.debug(
                  'ScoreCard',
                  'Scorecards and scores refreshed after group selection'
                );
                this.setFirstInputFocus();
              },
              error: (err) =>
                this.loggingService.error(
                  'ScoreCard',
                  'Error refreshing data after group selection',
                  err
                ),
            });
        } else {
          this.loggingService.debug(
            'ScoreCard',
            `Group with ID not found: ${queryGroupId}`
          );
        }
      }
    });

    // Effect for player selection
    const playerSelectionEffect = effect(() => {
      const playerId = this.queryPlayerIdSignal();
      this.loggingService.debug(
        'ScoreCard',
        `Player selection effect triggered with playerId: ${playerId}`
      );

      if (playerId && this.players.length > 0) {
        const player = this.players.find((p) => p.playerId === playerId);
        if (player) {
          this.loggingService.debug(
            'ScoreCard',
            `Setting selected player from query param: ${player.playFirstName} ${player.playLastName}`
          );
          // Update both legacy property and signal
          this._selectedPlayer = player;
          this.selectedPlayerSignal.set(player);
        } else {
          this.loggingService.debug(
            'ScoreCard',
            'Player not found in loaded players, will try to select after players load'
          );
        }
      } else if (!playerId) {
        this.loggingService.debug(
          'ScoreCard',
          'Clearing selected player (no playerId in query params)'
        );
        this._selectedPlayer = undefined;
        this.selectedPlayerSignal.set(undefined);
      }
    });

    // Register effects for clean-up
    this.destroyRef.onDestroy(() => {
      this.loggingService.debug('ScoreCard', 'Cleaning up effects');
      scorecardCountsEffect.destroy();
      coursesEffect.destroy();
      groupsEffect.destroy();
      initDataEffect.destroy();
      queryGroupEffect.destroy();
      playerSelectionEffect.destroy();

      // Clean up periodic processes
      this.stopPeriodicCleanup();

      // Clear local operations map
      this.localScoreOperations.clear();

      // Clear rate limiting maps
      this.alertRateLimit.clear();
      this.alertTimestamps.length = 0;
    });

    // Start periodic cleanup for local operations
    this.startPeriodicCleanup();

    // Set up validation error callback for score reversion
    this.scoreBufferService.setValidationErrorCallback((scoreId: string) => {
      this.handleScoreValidation(scoreId, true);
    });

    // Register refresh callback for alert container
    this.refreshService.setRefreshCallback(() => {
      this.loggingService.debug(
        'ScoreCard',
        'Refreshing scores from alert container'
      );
      this.fetchScores().subscribe({
        next: () => {
          this.loggingService.debug(
            'ScoreCard',
            'Scores refreshed successfully from alert action'
          );
        },
        error: (error) => {
          this.loggingService.error(
            'ScoreCard',
            'Error refreshing scores from alert action',
            error
          );
        },
      });
    });

    this.route.queryParamMap.subscribe((params) => {
      // Log the changed parameters in a clean format
      const paramObj: Record<string, string | null> = {};
      params.keys.forEach((key) => {
        paramObj[key] = params.get(key);
      });
      this.loggingService.debug('ScoreCard', 'Query params changed', paramObj);

      // Store previous values for comparison
      const prevGroupId = this.queryGroupId;

      // Update local properties
      this.queryRoundId = params.get('roundId');
      this.queryCourseId = params.get('courseId');
      this.queryGroupId = params.get('groupId');
      this.queryPlayerId = params.get('playerId');

      // Log group change specifically for debugging
      if (prevGroupId !== this.queryGroupId) {
        this.loggingService.debug(
          'ScoreCard',
          `Group ID changed from '${prevGroupId}' to '${this.queryGroupId}'`
        );
      }

      // Update the query signals to trigger effects
      this.queryGroupIdSignal.set(this.queryGroupId);
      this.queryPlayerIdSignal.set(this.queryPlayerId);
      this.loggingService.debug(
        'ScoreCard',
        `Updated player ID signal to: ${this.queryPlayerId}`
      );

      // Remove direct call to initializeData here
      if (this.isVerifyMode()) {
        this.initializeVerifyData();
        this.cancelScoreEdits();
      }
    });

    this.scoreChangeSubject
      .pipe(debounceTime(250))
      .subscribe(({ score, rowId, holeNumber }) => {
        this.setScore(score, rowId, holeNumber);
      });

    // Subscribe to scoring realtime events
    this.loggingService.debug(
      'ScoreCard',
      'Setting up scorecard activity subscription'
    );
    this.loggingService.debug(
      'ScoreCard',
      `SignalR service connection state: ${this.scoringRealtimeService.connectionState}`
    );
    this.loggingService.debug(
      'ScoreCard',
      `SignalR service is listening: ${this.scoringRealtimeService.isListening()}`
    );

    this.scoringRealtimeService.scorecardActivity
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // Add retry logic for resilience
        catchError((error) => {
          this.loggingService.error(
            'ScoreCard',
            'Error in scorecard activity subscription, will retry',
            error
          );
          // Return empty to continue the subscription
          return EMPTY;
        })
      )
      .subscribe({
        next: (activity) => {
          this.loggingService.debug(
            'ScoreCard',
            'Received scorecard activity from SignalR',
            activity
          );
          try {
            this.handleScorecardActivity(activity);
          } catch (error) {
            this.loggingService.error(
              'ScoreCard',
              'Error handling scorecard activity',
              error
            );
          }
        },
        error: (error) => {
          this.loggingService.error(
            'ScoreCard',
            'Fatal error in scorecard activity subscription',
            error
          );
        },
      });
  }

  /**
   * Handle incoming scorecard activity events from SignalR
   * Display toastr notification if the event is for a scorecard in the selected group
   */
  private handleScorecardActivity(activity: ScorecardActivityData): void {
    // Validate activity data first
    if (!activity) {
      this.loggingService.warn(
        'ScoreCard',
        'Received null or undefined activity data'
      );
      return;
    }

    this.loggingService.debug(
      'ScoreCard',
      'Received scorecard activity event',
      {
        ...activity,
        tournamentId: activity.tournamentId,
        roundId: activity.roundId,
        playerId: activity.playerId,
      }
    );

    if (!activity.scorecardId || typeof activity.scorecardId !== 'string') {
      this.loggingService.warn(
        'ScoreCard',
        'Activity missing valid scorecardId',
        activity
      );
      return;
    }

    if (!activity.type) {
      this.loggingService.warn('ScoreCard', 'Activity missing type', activity);
      return;
    }

    const selectedGroup = this.selectedGroupSignal();
    this.loggingService.debug(
      'ScoreCard',
      `Selected group: ${selectedGroup?.groupId}`
    );

    if (
      !selectedGroup ||
      !selectedGroup.slots ||
      selectedGroup.slots.length === 0
    ) {
      this.loggingService.debug(
        'ScoreCard',
        'No selected group or slots, skipping notification'
      );
      return;
    }

    // Check if the scorecard belongs to the selected group
    const isInSelectedGroup = selectedGroup.slots.some(
      (slot) => slot.scorecardId === activity.scorecardId
    );
    this.loggingService.debug(
      'ScoreCard',
      `Is scorecard in selected group? ${isInSelectedGroup} for scorecardId: ${activity.scorecardId}`
    );

    if (!isInSelectedGroup) {
      this.loggingService.debug(
        'ScoreCard',
        'Scorecard not in selected group, skipping notification'
      );
      return;
    }

    // Check rate limiting
    if (this.isAlertRateLimited(activity.scorecardId)) {
      this.loggingService.debug(
        'ScoreCard',
        'Alert rate limited, skipping notification'
      );
      return;
    }

    // Check if local operation suppression is enabled
    const suppressLocalOperations =
      this.configService.config?.notifications?.suppressLocalOperations ??
      false;

    if (suppressLocalOperations) {
      // Don't show notification for locally performed operations when suppression is enabled
      const suppressibleOperations = this.configService.config?.notifications
        ?.suppressibleOperations ?? [
        'ScorecardUpdated',
        'ScorecardMarkedOfficial',
        'ScorecardMarkedNotOfficial',
      ];
      const suppressionWindowMs =
        this.configService.config?.notifications?.suppressionWindowMs ?? 2000;

      if (
        suppressibleOperations.includes(activity.type) &&
        this.localScoreOperations.has(activity.scorecardId)
      ) {
        const timestamps = this.localScoreOperations.get(activity.scorecardId)!;
        const now = Date.now();

        // Check if any of the recent operations fall within the suppression window
        const recentOperation = timestamps.find((timestamp) => {
          const timeSinceOperation = now - timestamp;
          return timeSinceOperation < suppressionWindowMs;
        });

        if (recentOperation) {
          const timeSinceOperation = now - recentOperation;
          this.loggingService.debug(
            'ScoreCard',
            `Local operation found for ${activity.type}, time since operation: ${timeSinceOperation} ms`
          );
          this.loggingService.debug(
            'ScoreCard',
            'Suppressing notification due to recent local operation (suppression enabled)'
          );
          // Clean up expired operations periodically
          this.cleanupExpiredOperations();
          return;
        } else {
          this.loggingService.debug(
            'ScoreCard',
            'No recent local operations within suppression window, showing notification'
          );
          // All operations are outside the suppression window, but don't clean up yet
          // Let the periodic cleanup handle it to avoid race conditions
        }
      }
    } else {
      this.loggingService.debug(
        'ScoreCard',
        'Local operation suppression disabled, showing notification regardless of local operations'
      );
    }

    // Find the player for this scorecard using the new playerId from activity data
    let player = this.players.find((p) => p.playerId === activity.playerId);

    // Fallback to the original method if player not found by ID
    if (!player) {
      player = this.players.find((p) =>
        this.scorecards.some(
          (sc) =>
            sc.scorecardId === activity.scorecardId &&
            sc.playerId === p.playerId
        )
      );
      this.loggingService.debug(
        'ScoreCard',
        'Player found using fallback method',
        { playerId: player?.playerId }
      );
    } else {
      this.loggingService.debug(
        'ScoreCard',
        'Player found using activity playerId',
        { playerId: activity.playerId }
      );
    }

    const playerName =
      player?.firstName && player?.lastName
        ? `${player.firstName} ${player.lastName}`
        : 'Unknown Player';

    // Get tournament and round context for enhanced notifications
    const tournament = this.selectedTournament();
    const round = this.selectedRound();
    const tournamentName = tournament?.name || 'Tournament';
    const roundName = round?.number ? `Round ${round.number}` : 'Round';

    // Create appropriate toastr message based on activity type
    let message = '';
    let title = '';

    // Validate timestamp
    let timeString = 'unknown time';
    try {
      if (activity.timestamp && activity.timestamp instanceof Date) {
        timeString = new Intl.DateTimeFormat('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        }).format(activity.timestamp);
      } else if (activity.timestamp) {
        // Try to parse if it's not a Date object
        const parsedDate = new Date(activity.timestamp);
        if (!isNaN(parsedDate.getTime())) {
          timeString = new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
          }).format(parsedDate);
        }
      }
    } catch (error) {
      this.loggingService.warn(
        'ScoreCard',
        'Error formatting timestamp',
        error
      );
    }

    switch (activity.type) {
      case 'ScorecardUpdated':
        title = 'Scorecard Updated';
        message = `${playerName}'s scorecard for ${roundName} was updated at ${timeString}.`;
        break;
      case 'ScorecardCreated':
        title = 'Scorecard Created';
        message = `${playerName}'s scorecard for ${roundName} was created at ${timeString}.`;
        break;
      case 'ScorecardMarkedOfficial':
        title = 'Scorecard Marked Official';
        message = `${playerName}'s scorecard for ${roundName} was marked official at ${timeString}.`;
        break;
      case 'ScorecardMarkedNotOfficial':
        title = 'Scorecard Marked Not Official';
        message = `${playerName}'s scorecard for ${roundName} was marked as not official at ${timeString}.`;
        break;
      default:
        title = 'Scorecard Updated';
        message = `${playerName}'s scorecard for ${roundName} was updated at ${timeString}.`;
        break;
    }

    title = '';

    this.loggingService.debug('ScoreCard', 'Showing alert notification', {
      title,
      message,
    });

    // Check if alert service is available
    if (!this.alertService) {
      this.loggingService.error('ScoreCard', 'Alert service not available');
      return;
    }

    // Record this alert for rate limiting
    this.recordAlert(activity.scorecardId);

    // Use different alert types based on activity
    let alertId: string;
    try {
      switch (activity.type) {
        case 'ScorecardUpdated':
          alertId = this.alertService.info(title, message, {
            dismissible: true,
            autoClose: true,
            autoCloseDelay: 30000, // Auto-close after 30 seconds
            category: 'scorecard',
          });
          break;
        case 'ScorecardCreated':
          alertId = this.alertService.warning(title, message, {
            dismissible: true,
            autoClose: true,
            autoCloseDelay: 45000, // Auto-close after 45 seconds
            category: 'scorecard',
          });
          break;
        case 'ScorecardMarkedOfficial':
          alertId = this.alertService.success(title, message, {
            dismissible: true,
            autoClose: true,
            autoCloseDelay: 60000, // Auto-close after 60 seconds
            category: 'scorecard',
          });
          break;
        case 'ScorecardMarkedNotOfficial':
          alertId = this.alertService.warning(title, message, {
            dismissible: true,
            autoClose: true,
            autoCloseDelay: 45000, // Auto-close after 45 seconds
            category: 'scorecard',
          });
          break;
        default:
          alertId = this.alertService.info(title, message, {
            dismissible: true,
            autoClose: true,
            autoCloseDelay: 30000, // Auto-close after 30 seconds
            category: 'scorecard',
          });
          break;
      }

      this.loggingService.debug(
        'ScoreCard',
        `Alert notification shown successfully with ID: ${alertId}`
      );
    } catch (error) {
      this.loggingService.error(
        'ScoreCard',
        'Error showing alert notification',
        error
      );
      // Don't record the alert if it failed to show
      return;
    }
  }

  initializeData(): void {
    this.loggingService.debug(
      'ScoreCard',
      'initializeData: Starting data initialization'
    );

    // Use signals for tournament, course, and round
    const tournament = this.selectedTournament();
    const round = this.selectedRound();
    const course = this.selectedCourse(); // Get the course value from the signal

    this.loggingService.debug(
      'ScoreCard',
      `initializeData - Tournament: ${tournament?.name}`
    );
    this.loggingService.debug(
      'ScoreCard',
      `initializeData - Round: ${round ? `Round ${round.number}` : 'undefined'}`
    );
    this.loggingService.debug(
      'ScoreCard',
      `initializeData - Course: ${course?.courseName}`
    );

    if (!tournament || !round) {
      this.toasterService.info('Scorecards require a Tournament and Round.');
      this.loggingService.error(
        'ScoreCard',
        'Error: selectedTournament or selectedRound is undefined.'
      );

      this.courses = [];
      this.groups = [];
      this.groupSlots = [];
      this.selectedGroup = undefined;
      this.selectedGroupSignal.set(undefined);

      return;
    }

    this.loggingService.debug(
      'ScoreCard',
      'initializeData: Calling forkJoin for initial data load'
    );
    forkJoin({
      tournamentCourses:
        this.tournamentService.getTournamentCoursesByTournamentId(
          tournament.tournamentId!
        ),
      groups: this.fetchGroups(),
      holes: this.fetchHoles(),
      holeYardages: this.fetchHoleYardages(),
    })
      .pipe(
        tap(({ tournamentCourses, groups }) => {
          this.loggingService.debug(
            'ScoreCard',
            `initializeData: Received ${tournamentCourses.length} tournament courses`
          );
          this.loggingService.debug(
            'ScoreCard',
            `initializeData: Received ${
              groups?.length || 0
            } groups from fetchGroups`
          );
          this.courses = tournamentCourses;
        }),
        tap(({ groups }) => {
          // Ensure groups are set even if fetchGroups() returns an empty array
          if (!groups || groups.length === 0) {
            this.loggingService.debug(
              'ScoreCard',
              'initializeData: No groups returned, clearing group data'
            );
            this.groups = [];
            this.groupSlots = [];
            this.selectedGroup = undefined;
            this.selectedGroupSignal.set(undefined);
          } else {
            this.loggingService.debug(
              'ScoreCard',
              `initializeData: Groups array has ${groups.length} items`
            );
            this.loggingService.debug(
              'ScoreCard',
              `initializeData: Selected group is: ${
                this.selectedGroup?.number || 'undefined'
              }`
            );
            this.loggingService.debug(
              'ScoreCard',
              `initializeData: Group slots array has ${
                this.groupSlots?.length || 0
              } items`
            );
          }
        }),
        switchMap(() => {
          this.loggingService.debug(
            'ScoreCard',
            'initializeData: Fetching scorecards'
          );
          return this.fetchScorecards();
        }),
        switchMap(() => {
          this.loggingService.debug(
            'ScoreCard',
            'initializeData: Fetching scores'
          );
          return this.fetchScores();
        }),
        tap(() => {
          // Initialize scorecard counts for the progress bar
          this.loggingService.debug(
            'ScoreCard',
            'initializeData: Loading scorecard counts for progress bar'
          );
          if (round?.roundId) {
            this.refreshRoundScorecardCounts();
          }
        }),
        catchError((error) => {
          this.toasterService.error(
            'Error initializing data: ' + (error.message || 'Unknown error')
          );
          this.loggingService.error(
            'ScoreCard',
            'Error in initializeData',
            error
          );
          return EMPTY;
        }),
        finalize(() => {
          this.loggingService.debug(
            'ScoreCard',
            'initializeData: Finalized data initialization'
          );
        })
      )
      .subscribe({
        next: () => {
          this.loggingService.debug(
            'ScoreCard',
            'initializeData: Successfully completed, setting focus'
          );
          this.loggingService.debug(
            'ScoreCard',
            `Final state - Groups: ${this.groups.length}`
          );
          this.loggingService.debug(
            'ScoreCard',
            `Final state - Selected Group (legacy): ${
              this.selectedGroup?.number || 'undefined'
            }`
          );
          this.loggingService.debug(
            'ScoreCard',
            `Final state - Selected Group (signal): ${
              this.selectedGroupSignal()?.number || 'undefined'
            }`
          );
          this.loggingService.debug(
            'ScoreCard',
            `Final state - Group Slots: ${this.groupSlots.length}`
          );
          this.setFirstInputFocus();
        },
        error: (err) => {
          this.loggingService.error(
            'ScoreCard',
            'Unhandled error in initializeData',
            err
          );
        },
      });
  }

  fetchGroups(): Observable<Group[]> {
    const tournament = this.selectedTournament();
    const round = this.selectedRound();
    const course = this.selectedCourse(); // Get the course value from the signal

    this.loggingService.debug(
      'ScoreCard',
      `fetchGroups method - Tournament: ${tournament?.name}`
    );
    this.loggingService.debug(
      'ScoreCard',
      `fetchGroups method - Round: ${
        round ? `Round ${round.number}` : 'undefined'
      }`
    );
    this.loggingService.debug(
      'ScoreCard',
      `fetchGroups method - Course: ${course?.courseName}`
    );

    if (!tournament || !round || !course) {
      this.loggingService.debug(
        'ScoreCard',
        'fetchGroups: Missing required data, returning empty array'
      );
      return of([]);
    }

    this.loggingService.debug(
      'ScoreCard',
      `fetchGroups: Calling groupService.getByTournamentRoundCourse with: tournamentId=${tournament.tournamentId}, roundId=${round.roundId}, courseId=${course.courseId}`
    );

    return this.groupService
      .getByTournamentRoundCourse(
        tournament.tournamentId!,
        round.roundId!,
        course.courseId!
      )
      .pipe(
        tap((groups) => {
          this.loggingService.debug(
            'ScoreCard',
            `fetchGroups: Received ${groups.length} groups from API`
          );

          // Reset selected group (both signal and legacy property)
          this.selectedGroup = undefined;
          this.selectedGroupSignal.set(undefined);

          if (this.hideOfficialScorecards) {
            this.groups = groups.filter(
              (g) => g.groupNotOfficialScorecardCount! > 0
            );
            this.loggingService.debug(
              'ScoreCard',
              `fetchGroups: Filtered to ${this.groups.length} unofficial groups`
            );
          } else {
            this.groups = groups;
          }

          if (this.queryGroupId) {
            this.loggingService.debug(
              'ScoreCard',
              `fetchGroups: Looking for specific group by queryGroupId: ${this.queryGroupId}`
            );
            const foundGroup = this.groups.find(
              (g) => g.groupId === this.queryGroupId
            );

            // Update both signal and legacy property
            this.selectedGroup = foundGroup;
            this.selectedGroupSignal.set(foundGroup);

            this.loggingService.debug(
              'ScoreCard',
              `fetchGroups: Selected group by ID: ${
                foundGroup?.number || 'not found'
              }`
            );
          } else if (this.groups.length > 0) {
            // Update both signal and legacy property
            this.selectedGroup = this.groups[0];
            this.selectedGroupSignal.set(this.groups[0]);

            this.loggingService.debug(
              'ScoreCard',
              `fetchGroups: Selected first group: ${
                this.groups[0]?.number || 'none available'
              }`
            );
          }

          // Set groupSlots from the selected group
          this.groupSlots = this.selectedGroup?.slots || [];
          this.loggingService.debug(
            'ScoreCard',
            `fetchGroups: Set ${this.groupSlots.length} group slots from selected group`
          );
        })
      );
  }

  fetchHoles(): Observable<Hole[]> {
    const course = this.selectedCourse(); // Get the course value from the signal
    if (!course?.courseId) {
      return of([]);
    }
    return this.courseService.getHolesByCourse(course.courseId).pipe(
      tap((holes) => {
        this.holes = holes;
        const { holesOUT, holesIN } =
          this.utilitiesService.separateHolesByNine(holes);
        this.holesOUT = holesOUT;
        this.holesIN = holesIN;
      }),
      catchError((error) => {
        this.loggingService.error('ScoreCard', 'Error fetching holes', error);
        this.toasterService.error('Failed to fetch holes.');
        return of([]);
      })
    );
  }

  fetchHoleYardages(): Observable<HoleYardage[]> {
    const course = this.selectedCourse(); // Get the course value from the signal
    if (!course?.courseId) {
      return of([]);
    }
    return this.courseService.getHoleYardagesByCourse(course.courseId).pipe(
      tap((yardages) => {
        this.holeYardages = yardages;
      }),
      catchError((error) => {
        this.loggingService.error(
          'ScoreCard',
          'Error fetching hole yardages',
          error
        );
        this.toasterService.error('Failed to fetch hole yardages.');
        return of([]);
      })
    );
  }

  /**
   * Keyboard shortcuts configuration for scorecard navigation and actions
   */
  private readonly keyboardShortcuts: KeyboardShortcut[] = [
    {
      key: 'Enter',
      action: () => {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement?.tagName === 'BUTTON') {
          activeElement.click();
        } else {
          // In verify mode, if no input is focused, set focus to first input
          if (this.isVerifyMode() && !this.isInputCurrentlyFocused()) {
            this.setFirstInputFocus();
            return;
          } // In verify mode, stay on same row and move horizontally
          if (this.isVerifyMode()) {
            this.selectNextInputInVerifyMode('right');
          } else if (this.isStatsMode()) {
            // In stats mode, move to next input in sequence
            this.selectNextInput('right');
          } else {
            this.selectNextTab();
          }
        }
      },
    },
    {
      key: 'Tab',
      shiftKey: true,
      action: () => {
        // In verify mode, stay on same row and move horizontally left
        if (this.isVerifyMode()) {
          this.selectNextInputInVerifyMode('left');
        } else if (this.isStatsMode()) {
          // In stats mode, move to previous input in sequence
          this.selectNextInput('left');
        } else {
          this.selectPreviousTab();
        }
      },
    },
    {
      key: 'Tab',
      action: () => {
        // In verify mode, if no input is focused, set focus to first input
        if (this.isVerifyMode() && !this.isInputCurrentlyFocused()) {
          this.setFirstInputFocus();
          return;
        } // In verify mode, stay on same row and move horizontally right
        if (this.isVerifyMode()) {
          this.selectNextInputInVerifyMode('right');
        } else if (this.isStatsMode()) {
          // In stats mode, move to next input in sequence
          this.selectNextInput('right');
        } else {
          this.selectNextTab();
        }
      },
    },
    {
      key: 'h',
      ctrlKey: true,
      action: () => {
        if (!this.isVerifyMode()) return;
        this.hideOfficialScorecards = !this.hideOfficialScorecards;
        this.initializeData();
      },
    },
    {
      key: 'H',
      ctrlKey: true,
      action: () => {
        if (!this.isVerifyMode()) return;
        this.hideOfficialScorecards = !this.hideOfficialScorecards;
        this.initializeData();
      },
    },
    {
      key: 'o',
      ctrlKey: true,
      condition: () => this.canMarkOfficial(),
      action: () => this.markOfficial(),
    },
    {
      key: 'O',
      ctrlKey: true,
      condition: () => this.canMarkOfficial(),
      action: () => this.markOfficial(),
    },
    {
      key: 'ArrowRight',
      ctrlKey: true,
      action: () => this.selectNextPlayer(),
    },
    {
      key: 'ArrowRight',
      action: () => this.selectNextInput('right'),
    },
    {
      key: 'ArrowLeft',
      ctrlKey: true,
      action: () => this.selectPreviousPlayer(),
    },
    {
      key: 'ArrowLeft',
      action: () => this.selectNextInput('left'),
    },
    {
      key: 'ArrowDown',
      ctrlKey: true,
      action: () => this.selectNextGroup(),
    },
    {
      key: 'ArrowDown',
      action: () => this.selectNextInput('down'),
    },
    {
      key: 'ArrowUp',
      ctrlKey: true,
      action: () => this.selectPreviousGroup(),
    },
    {
      key: 'ArrowUp',
      action: () => this.selectNextInput('up'),
    },
    {
      key: 's',
      ctrlKey: true,
      action: () => this.playerSearchModal.openModal(),
    },
    {
      key: 'S',
      ctrlKey: true,
      action: () => this.playerSearchModal.openModal(),
    },
  ];

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    const shortcut = this.keyboardShortcuts.find(
      (s) =>
        s.key === event.key &&
        !!s.ctrlKey === event.ctrlKey &&
        !!s.shiftKey === event.shiftKey &&
        (!s.condition || s.condition())
    );

    if (shortcut) {
      event.preventDefault();
      shortcut.action();
    }
  }

  /**
   * Checks if an input element is disabled or unavailable for interaction
   * @param element The input element to check
   * @returns True if the element is disabled or unavailable, false otherwise
   */
  private isInputDisabled(element: HTMLInputElement): boolean {
    return (
      element.disabled ||
      element.readOnly ||
      element.getAttribute('disabled') !== null ||
      element.getAttribute('readonly') !== null ||
      element.hasAttribute('disabled') ||
      element.hasAttribute('readonly') ||
      element.classList.contains('readonly') ||
      element.style.pointerEvents === 'none' ||
      element.tabIndex === -1 ||
      element.offsetParent === null
    ); // Also check if element is visible
  }

  /**
   * Navigates to the next or previous input based on tabindex
   * @param direction Either 'next' or 'previous' to determine direction of movement
   */
  private selectTabByDirection(direction: 'next' | 'previous'): void {
    const activeElement = document.activeElement as HTMLInputElement;
    if (!activeElement?.getAttribute) {
      return;
    }

    const currentTabIndex = parseInt(
      activeElement.getAttribute('tabindex') || '0'
    );
    const indexChange = direction === 'next' ? 1 : -1;
    let targetTabIndex = currentTabIndex + indexChange;

    const inputs = this.scoreInputs.toArray();

    // Find the next enabled input by checking consecutive tab indices
    while (true) {
      const nextInput = inputs.find(
        (el) =>
          parseInt(el.nativeElement.getAttribute('tabindex') || '0') ===
          targetTabIndex
      );

      if (!nextInput) {
        // No more inputs in this direction
        break;
      }

      if (!this.isInputDisabled(nextInput.nativeElement)) {
        // Found an enabled input, focus on it
        requestAnimationFrame(() => {
          nextInput.nativeElement.focus();
          nextInput.nativeElement.select();
        });
        return;
      }

      // This input is disabled, try the next one
      targetTabIndex += indexChange;
    }
  }

  /**
   * Navigates to the next input based on tabindex
   */
  private selectNextTab(): void {
    this.selectTabByDirection('next');
  }

  /**
   * Navigates to the previous input based on tabindex
   */
  private selectPreviousTab(): void {
    this.selectTabByDirection('previous');
  }

  private selectNextInput(direction: string = 'right'): void {
    const inputs = this.scoreInputs.toArray();
    const activeElement = document.activeElement;
    const activeIndex = inputs.findIndex(
      (el) => el.nativeElement === activeElement
    );

    if (activeIndex === -1) return;

    let nextIndex: number;
    const HOLES_PER_ROUND = 18;
    let increment: number;

    switch (direction) {
      case 'right':
        nextIndex = activeIndex + 1;
        increment = 1;
        break;
      case 'left':
        nextIndex = activeIndex - 1;
        increment = -1;
        break;
      case 'up':
        nextIndex = activeIndex - HOLES_PER_ROUND;
        increment = -HOLES_PER_ROUND;
        break;
      case 'down':
        nextIndex = activeIndex + HOLES_PER_ROUND;
        increment = HOLES_PER_ROUND;
        break;
      default:
        return;
    }

    // Find the next enabled input in the specified direction
    while (nextIndex >= 0 && nextIndex < inputs.length) {
      const nextInput = inputs[nextIndex];

      if (!this.isInputDisabled(nextInput.nativeElement)) {
        requestAnimationFrame(() => {
          nextInput.nativeElement.focus();
          nextInput.nativeElement.select();
        });
        return;
      }

      nextIndex += increment;
    }
  }

  /**
   * Navigates horizontally in verify mode, staying on the same row and only moving to enabled inputs
   * @param direction 'right' to move right, 'left' to move left
   */
  private selectNextInputInVerifyMode(direction: 'right' | 'left'): void {
    const inputs = this.scoreInputs.toArray();
    const activeElement = document.activeElement;
    const activeIndex = inputs.findIndex(
      (el) => el.nativeElement === activeElement
    );

    if (activeIndex === -1) return;

    // Get current row identifier (row-id attribute)
    const currentInput = inputs[activeIndex];
    const currentRowId =
      currentInput?.nativeElement.getAttribute('data-row-id');

    if (!currentRowId) return;

    // Find all inputs in the same row
    const sameRowInputs = inputs.filter(
      (input) =>
        input.nativeElement.getAttribute('data-row-id') === currentRowId
    );

    // Find current position in the same row
    const currentRowIndex = sameRowInputs.findIndex(
      (input) => input.nativeElement === activeElement
    );

    if (currentRowIndex === -1) return;

    // Calculate next position
    const increment = direction === 'right' ? 1 : -1;
    let nextRowIndex = currentRowIndex + increment;

    // Find next enabled input in the same row
    while (nextRowIndex >= 0 && nextRowIndex < sameRowInputs.length) {
      const nextInput = sameRowInputs[nextRowIndex];
      const element = nextInput.nativeElement;

      if (!this.isInputDisabled(element)) {
        requestAnimationFrame(() => {
          element.focus();
          element.select();
        });
        return;
      }

      nextRowIndex += increment;
    }

    // If no enabled input found in the direction, stay at current position
  }

  /**
   * Selects a player by direction (next or previous) in the players array
   * @param direction 'next' to select the next player, 'previous' to select the previous player
   */
  private selectPlayerByDirection(direction: 'next' | 'previous'): void {
    const currentPlayer = this.selectedPlayerSignal();
    const currentIndex = this.players.findIndex(
      (player) => player.playerId === currentPlayer?.playerId
    );

    // if (currentIndex === -1) return;

    const indexChange = direction === 'next' ? 1 : -1;
    const newIndex = currentIndex + indexChange;

    if (newIndex >= 0 && newIndex < this.players.length) {
      this.selectPlayer(this.players[newIndex]);
    }
  }

  /**
   * Selects the next player in the players array
   */
  selectNextPlayer(): void {
    if (!this.isScoresMode()) {
      this.selectPlayerByDirection('next');
    }
  }

  /**
   * Selects the previous player in the players array
   */
  selectPreviousPlayer(): void {
    this.selectPlayerByDirection('previous');
  }

  /**
   * Checks if there is a next player available
   * @returns True if there is a next player, false otherwise
   */
  hasNextPlayer(): boolean {
    const currentPlayer = this.selectedPlayerSignal();
    const currentIndex = this.players.findIndex(
      (player) => player.playerId === currentPlayer?.playerId
    );
    return currentIndex < this.players.length - 1 && currentIndex !== -1;
  }

  /**
   * Checks if there is a previous player available
   * @returns True if there is a previous player, false otherwise
   */
  hasPreviousPlayer(): boolean {
    const currentPlayer = this.selectedPlayerSignal();
    const currentIndex = this.players.findIndex(
      (player) => player.playerId === currentPlayer?.playerId
    );
    return currentIndex > 0;
  }

  /**
   * Selects a group by direction (next or previous) in the groups array
   * @param direction 'next' to select the next group, 'previous' to select the previous group
   */
  private selectGroupByDirection(direction: 'next' | 'previous'): void {
    const currentGroup = this.selectedGroupSignal();
    const currentIndex = this.groups.findIndex(
      (group) => group.groupId === currentGroup?.groupId
    );

    if (currentIndex === -1) return;

    const indexChange = direction === 'next' ? 1 : -1;
    const newIndex = currentIndex + indexChange;

    if (newIndex >= 0 && newIndex < this.groups.length) {
      this.selectGroup(this.groups[newIndex]);
    }
  }

  /**
   * Selects the next group in the groups array
   */
  selectNextGroup(): void {
    this.selectGroupByDirection('next');
  }

  /**
   * Selects the previous group in the groups array
   */
  selectPreviousGroup(): void {
    this.selectGroupByDirection('previous');
  }

  /**
   * Checks if there is a next group available
   * @returns True if there is a next group, false otherwise
   */
  hasNextGroup(): boolean {
    const currentGroup = this.selectedGroupSignal();
    const currentIndex = this.groups.findIndex(
      (group) => group.groupId === currentGroup?.groupId
    );
    return currentIndex < this.groups.length - 1 && currentIndex !== -1;
  }

  /**
   * Checks if there is a previous group available
   * @returns True if there is a previous group, false otherwise
   */
  hasPreviousGroup(): boolean {
    const currentGroup = this.selectedGroupSignal();
    const currentIndex = this.groups.findIndex(
      (group) => group.groupId === currentGroup?.groupId
    );
    return currentIndex > 0;
  }

  /**
   * Initializes verify scores for both front nine (OUT) and back nine (IN)
   * Creates empty score objects with proper hole numbers
   */
  initializeVerifyData(): void {
    this.verifyScoresOUT = this.utilitiesService.createVerifyScores(1, 9);
    this.verifyScoresIN = this.utilitiesService.createVerifyScores(10, 18);
    this.setVerifyValid();
  }

  private fetchScorecard(scorecardId: string): Observable<ScorecardResponse> {
    return this.scorecardService
      .getScorecardById(scorecardId)
      .pipe(map((scorecard) => this.mapScorecardToDisplay(scorecard)));
  }

  fetchScorecards(): Observable<ScorecardResponse[]> {
    this.selectedPlayer = undefined;
    this.players = [];
    const scorecardIds = this.hideOfficialScorecards
      ? this.groupSlots
          .sort((a, b) => (a.number || 0) - (b.number || 0))
          .filter((slot) => slot.scorecardId && !slot.scorecardOfficial)
          .map((slot) => slot.scorecardId!)
      : this.groupSlots
          .sort((a, b) => (a.number || 0) - (b.number || 0))
          .filter((slot) => slot.scorecardId)
          .map((slot) => slot.scorecardId!);

    if (!scorecardIds.length) {
      this.scorecards = [];
      return of([]);
    }

    // Fetch all scorecards in parallel
    return forkJoin(scorecardIds.map((id) => this.fetchScorecard(id))).pipe(
      switchMap((scorecards) => {
        // Filter official scorecards if needed
        this.scorecards = this.hideOfficialScorecards
          ? scorecards.filter((s) => !s.official)
          : scorecards;

        // Extract unique player IDs
        const playerIds = [
          ...new Set(
            this.scorecards
              .sort((a, b) => {
                const slotA = this.groupSlots.find(
                  (slot) => slot.scorecardId === a.scorecardId
                );
                const slotB = this.groupSlots.find(
                  (slot) => slot.scorecardId === b.scorecardId
                );
                return (slotA?.number || 0) - (slotB?.number || 0);
              })
              .map((s) => s.playerId)
          ),
        ];

        // Only fetch players if we have scorecards
        if (playerIds.length === 0) {
          return of(this.scorecards);
        }

        // Get all players once and filter locally
        return this.playerService.getAll().pipe(
          map((allPlayers) => {
            this.players = playerIds
              .map((id) => allPlayers.find((p) => p.playerId === id))
              .filter((p): p is Player => p !== undefined);
            return this.scorecards;
          }),
          catchError((error) => {
            this.loggingService.error(
              'ScoreCard',
              'Error fetching players',
              error
            );
            return of(this.scorecards);
          }),
          finalize(() => {
            this.loggingService.debug(
              'ScoreCard',
              `Players loaded: ${this.players.length}`
            );
            if (this.queryPlayerId) {
              this.loggingService.debug(
                'ScoreCard',
                `Looking for player with ID: ${this.queryPlayerId}`
              );
              const player = this.players.find(
                (p) => p.playerId === this.queryPlayerId
              );

              if (player) {
                this.loggingService.debug(
                  'ScoreCard',
                  `Found player by query param ID: ${player.playFirstName} ${player.playLastName}`
                );
                // Update both the legacy property and the signal
                this.selectedPlayer = player;
                this.selectedPlayerSignal.set(player);
              } else {
                this.loggingService.debug(
                  'ScoreCard',
                  `Player with ID not found: ${this.queryPlayerId}`
                );
              }
            } else {
              if (!this.isScoresMode() && this.players.length > 0) {
                this.loggingService.debug(
                  'ScoreCard',
                  'Setting first player as selected in non-scores mode'
                );
                // Update both the legacy property and the signal
                this.selectedPlayer = this.players[0];
                this.selectedPlayerSignal.set(this.players[0]);
                this.updateSelection('playerId', this.players[0]?.playerId);
              }
            }

            // Subscribe to SignalR events for players in the selected group (after scorecards are loaded)
            if (this.selectedGroup && this.scorecards.length > 0) {
              this.subscribeToSelectedGroupPlayers(this.selectedGroup);
            }
          })
        );
      })
    );
  }

  /**
   * Maps score response objects to include formatted display values for UI presentation
   * @param scores Array of score responses from the API
   * @returns Array of score responses with additional display properties
   */
  private mapScoresDisplay(scores: ScoreResponse[]): ScoreResponse[] {
    return this.utilitiesService.mapScoresToDisplayFormat(
      scores,
      (holeId: string) => this.hasFairway(holeId),
      (holeId: string) => this.isDriveMeasured(holeId)
    );
  }

  fetchScorecardScores(scorecardId: string): Observable<ScoreResponse[]> {
    return this.scorecardService.getScores(scorecardId).pipe(
      tap((scores) => {
        const mappedScores = this.mapScoresDisplay(scores);

        // Add missing scores to the main scores array
        this.scores[scorecardId] = this.addMissingScores(
          mappedScores,
          scorecardId,
          1,
          SCORECARD_CONSTANTS.HOLES_PER_ROUND
        );

        // Set scoresOUT and scoresIN based on the updated scores array
        this.scoresOUT[scorecardId] = this.scores[scorecardId].filter(
          (score) => score.holeNumber !== undefined && score.holeNumber <= 9
        );
        this.scoresIN[scorecardId] = this.scores[scorecardId].filter(
          (score) => score.holeNumber !== undefined && score.holeNumber > 9
        );

        this.setCumulativePlusMinus(scorecardId);
      })
    );
  }

  private updateCumulativePlusMinus(
    scores: ScoreResponse[],
    holes: Hole[],
    startPlusMinus: number = 0
  ): number {
    const result = this.calculationService.calculateCumulativePlusMinus({
      scores,
      holes,
      startPlusMinus,
    });

    // Update the original scores array with the calculated values
    scores.forEach((score, index) => {
      const updatedScore = result.updatedScores[index];
      score.plusMinus = updatedScore.plusMinus;
      score.plusMinusDisplay = updatedScore.plusMinusDisplay;
    });

    return result.finalCumulativePlusMinus;
  }

  private addMissingScores(
    scores: ScoreResponse[],
    scorecardId: string,
    start: number,
    end: number
  ): ScoreResponse[] {
    for (let holeNumber = start; holeNumber <= end; holeNumber++) {
      if (!scores.find((s) => s.holeNumber === holeNumber)) {
        const holeId = this.holes.find((h) => h.number === holeNumber)?.holeId;

        const holeYardage = this.holeYardages.find(
          (h) => h.holeId === holeId
        )?.yardage;

        scores.push({
          isNew: true,
          scoreId: crypto.randomUUID(),
          scorecardId,
          holeId,
          holeNumber,
          holeYardage,
          score: undefined,
          scoreType: 1,
          matchResult: 0,
          sfpoints: 0,
          fairway: true,
          fairwayDisplay: '',
          gir: true,
          girDisplay: '',
          sandSaveAttempted: false,
          sandSaveMade: false,
          sandSaveMadeDisplay: '',
          putts: undefined,
          driveDistance: undefined,
          statsEntered: false,
          submittedAt: undefined,
          submittedBy: undefined,
          statusCode: 'I',
          verified: false,
          official: false,
          plusMinus: undefined,
          shots: [],
        } as ScoreResponse);
      }
    }
    return scores.sort((a, b) => a.holeNumber! - b.holeNumber!);
  }

  fetchScores(): Observable<ScoreResponse[]> {
    // Flush buffer before fetching fresh data
    return this.scoreBufferService.flushBuffer().pipe(
      switchMap(() => {
        // First fetch scorecards, then fetch scores for those scorecards
        return this.fetchScorecards().pipe(
          switchMap((scorecards) => {
            const scorecardIds = scorecards.map(
              (scorecard) => scorecard.scorecardId!
            );

            if (scorecardIds.length === 0) {
              // No scorecards to fetch scores for
              this.toasterService.info('No scorecards found');
              return of([]);
            }

            return forkJoin(
              scorecardIds.map((scorecardId) =>
                this.fetchScorecardScores(scorecardId)
              )
            ).pipe(
              map(() => {
                const allScores = Object.values(this.scores).reduce(
                  (acc, scores) => acc.concat(scores),
                  [] as ScoreResponse[]
                );

                allScores.forEach((score) => {
                  const scorecard = this.updateScorecardCalcs(score);
                  this.scorecards = this.mapScorecardsToDisplay(scorecard!);
                });

                let message = this.isStatsMode()
                  ? 'Scores and Stats loaded'
                  : 'Scores loaded';

                this.toasterService.info(message);

                return allScores;
              })
            );
          })
        );
      })
    );
  }

  /**
   * Refreshes scores and clears any validation error states
   */
  refreshScores(): void {
    // Clear validation errors when refreshing
    this.hasScoreValidationError = {};

    // Fetch fresh scores
    this.fetchScores().subscribe();
  }

  private displayWarning() {
    const config = {
      timeOut: this.configService.config?.toastr.timeOut!,
      positionClass: this.configService.config?.toastr.positionClass!,
    } as IndividualConfig;
    switch (this.scorecardMode) {
      case 'stats':
        this.toasterService.warning(
          'You have unsaved Scores and Stats. Please click Submit or Reload before continuing.',
          'Warning',
          config
        );
        break;
      case 'verify':
        this.toasterService.warning(
          'You have unsaved Scores. Please click Save Scores or Cancel Edits before continuing.',
          'Warning',
          config
        );
        break;
    }
  }

  private checkForDirtyScores(): boolean {
    if (!this.isScoresMode()) {
      // Check buffer in addition to local dirty state
      if (
        this.scoreBufferService.hasPendingChanges() ||
        this.dirtyScoreCount() > 0
      ) {
        this.displayWarning();
        return true;
      }
    }
    return false;
  }

  selectGroup(group: Group) {
    if (this.checkForDirtyScores()) return;

    this.selectedPlayer = undefined;

    // Update both the signal and the legacy property for transition period
    this.selectedGroupSignal.set(group);
    this.selectedGroup = group;

    // Set up group slots for the selected group
    this.groupSlots = group?.slots || [];

    this.loggingService.debug(
      'ScoreCard',
      `Group selected: ${group.number}, Group ID: ${group.groupId}`
    );

    // Update the query params to persist selection
    this.updateSelection('groupId', group?.groupId!, ['playerId']);

    // After group selection, fetch scorecards and scores, then set focus
    this.fetchScorecards()
      .pipe(switchMap(() => this.fetchScores()))
      .subscribe({
        next: () => {
          this.loggingService.debug(
            'ScoreCard',
            'Scorecards and scores refreshed after group selection'
          );

          // Subscribe to SignalR events for players in the selected group
          if (group) {
            this.subscribeToSelectedGroupPlayers(group);
          }

          // Set focus on the first available blank score based on start tee
          if (this.isScoresMode()) {
            this.setFirstInputFocus();
          }
        },
        error: (err) =>
          this.loggingService.error(
            'ScoreCard',
            'Error refreshing data after group selection',
            err
          ),
      });
  }

  selectPlayer(player: Player) {
    if (this.checkForDirtyScores()) return;

    this.loggingService.debug(
      'ScoreCard',
      `Selecting player: ${player?.playFirstName} ${player?.playLastName}`
    );
    // The signal will be updated via effect when query param changes
    this.updateSelection('playerId', player?.playerId!);
  }

  // Add courses property to hold the list of tournament courses
  courses: TournamentCourse[] = [];

  // Add selectCourse method to update the selected course via TournamentService
  selectCourse(course: TournamentCourse): void {
    if (this.checkForDirtyScores()) return;
    if (!course?.courseId) {
      this.loggingService.warn(
        'ScoreCard',
        'Attempted to select course without id'
      );
      return;
    }

    this.loggingService.debug(
      'ScoreCard',
      `Selecting course: ${course.courseName}`
    );

    // Update local state immediately so UI reacts before the router completes navigation
    this.queryCourseId = course.courseId;
    this.selectedCourse.set(course);

    // Reset dependent selections so the subsequent initialize cycle starts clean
    this.selectedGroup = undefined;
    this.selectedGroupSignal.set(undefined);
    this.groupSlots = [];
    this.players = [];
    this.selectedPlayer = undefined;
    this.queryGroupIdSignal.set(null);
    this.queryPlayerIdSignal.set(null);

    this.updateSelection('courseId', course.courseId, [
      'roundId',
      'groupId',
      'playerId',
    ]);
  }

  updateSelection(
    paramName: string,
    value: string | undefined,
    clear: string[] = []
  ): void {
    const queryParams: Params = { ...this.route.snapshot?.queryParams };

    queryParams[paramName] = value;

    if (clear.length > 0) {
      clear.forEach((param) => {
        queryParams[param] = null;
      });
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  private getTotalForScores(scores: ScoreResponse[]): number | string {
    return this.utilitiesService.calculateScoresTotalWithFallback(
      scores,
      this.calculationService
    );
  }

  getScoresOUTTotal(scorecardId: string): number | string {
    return this.getTotalForScores(this.scoresOUT[scorecardId!]);
  }

  getScoresINTotal(scorecardId: string): number | string {
    return this.getTotalForScores(this.scoresIN[scorecardId!]);
  }

  private calcHolePlusMinus(score: ScoreResponse, hole: Hole): number {
    return this.calculationService.calculateHolePlusMinus(
      score.score!,
      hole.par!
    );
  }

  private findScoreInput(scoreInput: ScoreInput): HTMLInputElement | undefined {
    return this.scoreInputs.find((el) => {
      const element = el.nativeElement as HTMLInputElement;
      return (
        element.getAttribute('data-row-id') === scoreInput.rowId &&
        Number(element.getAttribute('data-hole-number')) ===
          scoreInput.holeNumber
      );
    })?.nativeElement;
  }

  hasFairway(holeId: string): boolean {
    const hole = this.holes.find((h) => h.holeId === holeId);
    return hole ? this.utilitiesService.shouldTrackFairway(hole.par!) : false;
  }

  isDriveMeasured(holeId: string): boolean {
    return this.utilitiesService.isDriveMeasuredForHole(holeId, this.holes);
  }

  private handleScoreValidation(scoreId: string, hasError: boolean): void {
    this.hasScoreValidationError[scoreId] = hasError;

    if (hasError) {
      // Remove from processing set to prevent focus advancement
      this.scoresBeingProcessed.delete(scoreId);
      this.loggingService.debug(
        'ScoreCard',
        `Validation error for score ${scoreId}, preventing focus advancement`
      );
    }
  }

  /**
   * Check if a score has a validation error
   */
  hasValidationError(score: ScoreResponse): boolean {
    return this.hasScoreValidationError[score.scoreId!] || false;
  }

  dirtyScoreCount(): number {
    return this.utilitiesService.countDirtyScores(this.scores);
  }

  isOUTTotalDirty(scorecardId: string): boolean {
    const scores = this.scoresOUT[scorecardId];
    return this.utilitiesService.isOUTTotalDirty(scores);
  }

  isINTotalDirty(scorecardId: string): boolean {
    const scores = this.scoresIN[scorecardId];
    return this.utilitiesService.isINTotalDirty(scores);
  }

  isRoundTotalDirty(scorecardId: string): boolean {
    const scoresOUT = this.scoresOUT[scorecardId];
    const scoresIN = this.scoresIN[scorecardId];
    return this.utilitiesService.isRoundTotalDirty(scoresOUT, scoresIN);
  }

  isTotalDirty(scorecardId: string): boolean {
    const scoresOUT = this.scoresOUT[scorecardId];
    const scoresIN = this.scoresIN[scorecardId];
    return this.utilitiesService.isTotalDirty(scoresOUT, scoresIN);
  }

  private updateScorecardCalcs(
    currentScore: ScoreResponse
  ): ScorecardResponse | undefined {
    if (!currentScore.scorecardId || !currentScore.scoreId) {
      return;
    }

    const scorecardId = currentScore.scorecardId;
    const currentScorecard = this.scorecards.find(
      (s) => s.scorecardId === scorecardId
    );

    if (!currentScorecard) {
      return;
    }

    const scorecardScores = this.scores[scorecardId] || [];
    const totals = this.calculationService.calculateScorecardTotals(
      scorecardScores,
      this.holes,
      currentScorecard.startScore || 0,
      currentScorecard.startPlusMinus || 0
    );

    // Adjust for new score if it hasn't been persisted yet
    let adjustedRoundScore = totals.roundScore;
    if (!currentScore.createdOnUtc) {
      adjustedRoundScore += Number(currentScore.score || 0);
    }

    const scorecard = {
      ...currentScorecard,
      roundPlusMinus: totals.roundPlusMinus,
      roundScore: adjustedRoundScore,
      finishScore: adjustedRoundScore + (currentScorecard.startScore || 0),
      finishPlusMinus:
        totals.roundPlusMinus + (currentScorecard.startPlusMinus || 0),
    } as ScorecardResponse;

    return scorecard;
  }

  saveDirtyScores(): void {
    const scorecard = this.getFilteredScorecard();

    const scores = this.scores[scorecard?.scorecardId!];

    scores.forEach((score) => {
      if (
        score.isDirty ||
        score.isDriveDistanceDirty ||
        score.isPuttsDirty ||
        score.isFairwayDirty ||
        score.isGirDirty ||
        score.isSandSaveDirty
      ) {
        // Add to buffer instead of direct save
        this.scoreBufferService.bufferScore(score, scorecard!);
      }
    });

    // Flush the buffer when explicitly saving
    this.scoreBufferService.flushBuffer().subscribe(() => {
      // Refresh data after flush completes
      this.fetchScores().subscribe();
      this.verifyScoreMode = 'view';
      this.setVerifyValid();
    });
  }

  private setCumulativePlusMinus(scorecardId: string): void {
    const final = this.updateCumulativePlusMinus(
      this.scoresOUT[scorecardId],
      this.holesOUT,
      this.scorecards.find((s) => s.scorecardId === scorecardId)?.startPlusMinus
    );
    this.updateCumulativePlusMinus(
      this.scoresIN[scorecardId],
      this.holesIN,
      final
    );
  }

  setStat(score: ScoreResponse, rowId: string, holeNumber: number) {
    this.activeScoreInput = { rowId, holeNumber };
    switch (rowId) {
      case 'fairway':
        score.fairway = score.fairwayDisplay?.toLocaleUpperCase() === 'Y';
        score.isFairwayDirty = score.originalFairway! !== score.fairway;
        break;
      case 'sandSaveMade':
        const ssMadeDisplay = score.sandSaveMadeDisplay?.toLocaleUpperCase();
        score.sandSaveAttempted = ssMadeDisplay !== '';
        score.sandSaveMade = ssMadeDisplay === 'Y';
        score.isSandSaveDirty =
          score.originalSandSaveMade! !== score.sandSaveMade &&
          score.originalSandSaveAttempted! !== score.sandSaveAttempted;
        break;
      case 'gir':
        score.gir = score.girDisplay?.toLocaleUpperCase() === 'Y';
        score.isGirDirty = score.originalGir! !== score.gir!;
        break;
      case 'driveDistance':
        score.driveDistance =
          +score.driveDistanceDisplay! > 0 ? +score.driveDistanceDisplay! : 0;
        score.isDriveDistanceDirty =
          +score.originalDriveDistance! !== +score.driveDistance!;
        break;
      case 'putts':
        score.putts = score.putts ? +score.putts! : 0;
        score.isPuttsDirty = +score.originalPutts! !== +score.putts!;
        break;
    }

    this.restoreFocus();
  }

  setScore(score: ScoreResponse, rowId: string, holeNumber: number): void {
    // Validate that the score is not 0 (invalid golf score)
    if (score.score !== null && score.score !== undefined) {
      if (!this.utilitiesService.isValidScore(score.score)) {
        this.loggingService.warn(
          'ScoreCard',
          `Invalid score attempted: ${score.score} for hole ${holeNumber}. Reverting to original score.`
        );
        // Revert to original score if invalid
        score.score = score.originalScore;
        return;
      }
    }

    // In the Hole or Score changed or Shots changed
    const isDirty =
      +score.originalScore! !== +score.score! ||
      score.originalStatusCode !== score.statusCode ||
      score.shots?.some((shot) => shot.shotId === 'selected');

    if (!isDirty) return;

    this.loggingService.debug(
      'ScoreCard',
      `Setting score for hole ${holeNumber}, value: ${score.score}, mode: ${this.scorecardMode}`
    );

    this.activeScoreInput = { rowId, holeNumber };

    const isScoreCleared = !score.score?.toString().trim();
    if (isScoreCleared) {
      score.isNew = true;
    }

    const scorecard = this.updateScorecardCalcs(score);

    if (this.isScoresMode()) {
      // Find the hole for plus/minus calculation
      const hole = this.findScoreHole(score);

      this.loggingService.debug(
        'ScoreCard',
        'Buffering score for immediate processing'
      );

      // Track that this score is being processed
      this.scoresBeingProcessed.add(score.scoreId!);

      // Track this scorecard ID as a local operation to prevent showing notifications (only if suppression is enabled)
      const suppressLocalOperations =
        this.configService.config?.notifications?.suppressLocalOperations ??
        false;
      if (suppressLocalOperations) {
        const timestamps =
          this.localScoreOperations.get(score.scorecardId!) || [];
        timestamps.push(Date.now());

        // Keep only the last 10 operations per scorecard to prevent memory issues
        if (timestamps.length > 10) {
          timestamps.splice(0, timestamps.length - 10);
        }

        this.localScoreOperations.set(score.scorecardId!, timestamps);

        // IMPORTANT: Also register with SignalR service for consistent suppression
        this.scoringRealtimeService.registerLocalScorecard(score.scorecardId!);
      }

      // Use buffer instead of direct API call for smoother UX
      this.scoreBufferService.bufferScore(score, scorecard!, hole);

      // Update local display immediately for responsive feel
      this.setCumulativePlusMinus(score.scorecardId!);
      this.scorecards = this.mapScorecardsToDisplay(scorecard!);

      // Advance focus for smooth navigation, but delay to allow validation check
      setTimeout(() => {
        // Only advance if this score is not currently in error state
        if (
          !this.hasValidationError(score) &&
          this.scoresBeingProcessed.has(score.scoreId!)
        ) {
          this.setNextHoleFocus(holeNumber + 1);
        }

        // Clean up tracking regardless of success/failure after a reasonable delay
        setTimeout(() => {
          this.scoresBeingProcessed.delete(score.scoreId!);
        }, 1000);
      }, 100); // Increased delay to allow time for validation response
    } else {
      score.isDirty = isDirty;

      this.setCumulativePlusMinus(score.scorecardId!);

      this.scorecards = this.mapScorecardsToDisplay(scorecard!);

      this.setVerifyValid();
      this.restoreFocus();
    }
  }

  onScoreChange(score: ScoreResponse, rowId: string, holeNumber: number): void {
    if (this.isScoresMode()) {
      // In scores mode, process immediately without debounce for smooth UX
      this.setScore(score, rowId, holeNumber);
    } else {
      // In other modes, use debounced processing
      this.scoreChangeSubject.next({ score, rowId, holeNumber });
    }
  }

  private mapScorecardsToDisplay(
    updatedScorecard: ScorecardResponse
  ): ScorecardResponse[] {
    return this.scorecards.map((scorecard) =>
      scorecard.scorecardId === updatedScorecard?.scorecardId
        ? this.mapScorecardToDisplay(updatedScorecard)
        : scorecard
    );
  }

  private saveScore(
    scorecard: ScorecardResponse,
    score: ScoreResponse
  ): Observable<any> {
    // Track this scorecard ID as a local operation to prevent showing notifications (only if suppression is enabled)
    const suppressLocalOperations =
      this.configService.config?.notifications?.suppressLocalOperations ??
      false;
    if (suppressLocalOperations) {
      const timestamps =
        this.localScoreOperations.get(scorecard.scorecardId!) || [];
      timestamps.push(Date.now());

      // Keep only the last 10 operations per scorecard to prevent memory issues
      if (timestamps.length > 10) {
        timestamps.splice(0, timestamps.length - 10);
      }

      this.localScoreOperations.set(scorecard.scorecardId!, timestamps);

      // IMPORTANT: Also register with SignalR service for consistent suppression
      this.scoringRealtimeService.registerLocalScorecard(
        scorecard.scorecardId!
      );
    }

    // Store the current hole number for focus advancement before async operations
    const nextHole = score.holeNumber! + 1;

    const scoreOperation$ = !score.score?.toString().trim()
      ? this.scorecardService.deleteScore(
          scorecard.scorecardId!,
          score.scoreId!
        )
      : this.updateScore(score);

    return zip([scoreOperation$, of(scorecard)]).pipe(
      tap(([_, scorecard]) => {
        this.handleScoreValidation(score.scoreId!, false);
        this.scorecards = this.mapScorecardsToDisplay(scorecard);

        // Clean up processing tracking on successful save
        this.scoresBeingProcessed.delete(score.scoreId!);
      }),
      switchMap(() => this.fetchScorecardScores(scorecard.scorecardId!)),
      tap(() => {
        // Advance focus immediately after scores are refreshed but before finalize
        // Add small delay to ensure DOM is stable after score updates
        setTimeout(() => {
          this.setNextHoleFocus(nextHole);
        }, 100);
      }),
      finalize(() => {
        this.setVerifyValid();
        const statsModeMessage = this.isStatsMode() ? ' and Stats ' : ' ';
        this.toasterService.success(
          `Scores${statsModeMessage}saved for ${scorecard.playerLastName}, ${scorecard.playerFirstName}`
        );
      }),
      catchError((error: ApiValidationError) => {
        this.handleScoreValidation(score.scoreId!, true);
        // Remove from local operations set if save fails
        this.localScoreOperations.delete(scorecard.scorecardId!);
        return EMPTY;
      })
    );
  }

  private restoreFocus(): void {
    if (!this.activeScoreInput) {
      return;
    }

    requestAnimationFrame(() => {
      const input = this.findScoreInput(this.activeScoreInput!);

      if (input && !this.isInputDisabled(input)) {
        // Target input is enabled, focus it normally
        input.focus();
      } else {
        // Target input is disabled/readonly, find the next enabled input
        this.findAndFocusNextEnabledInput();
      }
    });
  }

  /**
   * Finds and focuses the next enabled input when the intended target is disabled
   */
  private findAndFocusNextEnabledInput(): void {
    if (!this.activeScoreInput) return;

    const inputs = this.scoreInputs.toArray();
    const targetInput = this.findScoreInput(this.activeScoreInput);

    if (!targetInput) return;

    // Get the tabindex of the target (possibly disabled) input
    const targetTabIndex = parseInt(
      targetInput.getAttribute('tabindex') || '0'
    );
    let searchTabIndex = targetTabIndex + 1;

    // Search for the next enabled input with consecutive tabindex values
    for (let attempts = 0; attempts < 50; attempts++) {
      const nextInput = inputs.find(
        (el) =>
          parseInt(el.nativeElement.getAttribute('tabindex') || '0') ===
          searchTabIndex
      );

      if (!nextInput) {
        searchTabIndex++;
        continue;
      }

      if (!this.isInputDisabled(nextInput.nativeElement)) {
        // Found an enabled input, focus it and update activeScoreInput
        const rowId = nextInput.nativeElement.getAttribute('data-row-id');
        const holeNumber = parseInt(
          nextInput.nativeElement.getAttribute('data-hole-number') || '0'
        );

        if (rowId && holeNumber) {
          this.activeScoreInput = { rowId, holeNumber };
          nextInput.nativeElement.focus();
          nextInput.nativeElement.select();
        }
        return;
      }

      searchTabIndex++;
    }
  }

  setNextHoleFocus(hole: number) {
    if (this.scorecardMode === 'scores' && this.activeScoreInput) {
      if (this.scoringType === 'scorecard') {
        // Scorecard mode: move to next hole for same player
        this.activeScoreInput.holeNumber = hole;
        this.restoreFocus();
      } else if (this.scoringType === 'group') {
        // Group mode: move to next player for same hole, or next hole if no more players
        this.findNextAvailableInputInGroupMode();
      }
    }
  }

  /**
   * Finds the next available input in group scoring mode
   * In group mode, focus should move to the next enabled player on the same hole,
   * or to the first enabled player on the next hole if no more players available
   */
  private findNextAvailableInputInGroupMode(): void {
    if (!this.activeScoreInput) return;

    const inputs = this.scoreInputs.toArray();
    const currentInput = this.findScoreInput(this.activeScoreInput);

    if (!currentInput) return;

    const currentTabIndex = parseInt(
      currentInput.getAttribute('tabindex') || '0'
    );
    let searchTabIndex = currentTabIndex + 1;

    // Search for the next enabled input with consecutive tabindex values
    for (let attempts = 0; attempts < 200; attempts++) {
      // Increased limit for group mode with official scorecards
      const nextInput = inputs.find(
        (el) =>
          parseInt(el.nativeElement.getAttribute('tabindex') || '0') ===
          searchTabIndex
      );

      if (!nextInput) {
        searchTabIndex++;
        continue;
      }

      // Check if the input is enabled and not from an official scorecard
      if (
        !this.isInputDisabled(nextInput.nativeElement) &&
        !this.isInputFromOfficialScorecard(nextInput.nativeElement)
      ) {
        // Found an enabled input from a non-official scorecard, focus it and update activeScoreInput
        const rowId = nextInput.nativeElement.getAttribute('data-row-id');
        const holeNumber = parseInt(
          nextInput.nativeElement.getAttribute('data-hole-number') || '0'
        );

        if (rowId && holeNumber) {
          this.activeScoreInput = { rowId, holeNumber };

          requestAnimationFrame(() => {
            nextInput.nativeElement.focus();
            nextInput.nativeElement.select();

            // Verify focus was actually set
            setTimeout(() => {
              const focused = document.activeElement;
              const isFocused = focused === nextInput.nativeElement;
              this.loggingService.debug(
                'ScoreCard',
                `Focus verification - Expected: Player ${rowId}, Hole ${holeNumber}, Actually focused: ${isFocused}`
              );
              if (!isFocused && focused) {
                this.loggingService.debug(
                  'ScoreCard',
                  'Focus is actually on:',
                  {
                    tagName: focused.tagName,
                    rowId: focused.getAttribute('data-row-id'),
                    holeNumber: focused.getAttribute('data-hole-number'),
                    tabIndex: focused.getAttribute('tabindex'),
                  }
                );
              }
            }, 50);
          });
        }
        return;
      }

      searchTabIndex++;
    }

    // If no enabled input found in forward direction, try wrapping to beginning of next hole
    this.findNextHoleFirstEnabledInput();
  }

  /**
   * Finds the first enabled input on the next hole when no more players are available on current hole
   */
  private findNextHoleFirstEnabledInput(): void {
    if (!this.activeScoreInput) return;

    const currentHole = this.activeScoreInput.holeNumber;
    const nextHole = currentHole >= 18 ? 1 : currentHole + 1; // Wrap to hole 1 after hole 18

    // Find all inputs for the next hole that are not from official scorecards
    const inputs = this.scoreInputs.toArray();
    const nextHoleInputs = inputs.filter((input) => {
      const element = input.nativeElement;
      const holeNumber = parseInt(
        element.getAttribute('data-hole-number') || '0'
      );
      return (
        holeNumber === nextHole &&
        !this.isInputDisabled(element) &&
        !this.isInputFromOfficialScorecard(element)
      );
    });

    // Sort by tab index to get the correct order
    nextHoleInputs.sort((a, b) => {
      const tabA = parseInt(a.nativeElement.getAttribute('tabindex') || '0');
      const tabB = parseInt(b.nativeElement.getAttribute('tabindex') || '0');
      return tabA - tabB;
    });

    // Focus the first available input on the next hole
    if (nextHoleInputs.length > 0) {
      const firstInput = nextHoleInputs[0];
      const rowId = firstInput.nativeElement.getAttribute('data-row-id');
      const holeNumber = parseInt(
        firstInput.nativeElement.getAttribute('data-hole-number') || '0'
      );

      if (rowId && holeNumber) {
        this.activeScoreInput = { rowId, holeNumber };

        requestAnimationFrame(() => {
          firstInput.nativeElement.focus();
          firstInput.nativeElement.select();
          this.loggingService.debug(
            'ScoreCard',
            `Wrapped to hole ${nextHole}, focus set to Player: ${rowId}`
          );
        });
      }
    }
  }

  /**
   * Checks if an input belongs to an official scorecard and should be skipped during focus advancement
   */
  private isInputFromOfficialScorecard(element: HTMLInputElement): boolean {
    const rowId = element.getAttribute('data-row-id');
    if (!rowId) return false;

    // For score inputs, the rowId is the playerId
    const scorecard = this.scorecards.find((sc) => sc.playerId === rowId);
    return scorecard?.official || false;
  }

  private findScoreHole(score: ScoreResponse): Hole | undefined {
    const hole = this.holes.find((h) => h.holeId === score.holeId);
    return hole;
  }

  private updateScore(score: ScoreResponse): Observable<any> {
    const hole = this.findScoreHole(score);
    if (!hole) return EMPTY;

    const plusMinus = this.calcHolePlusMinus(score, hole);

    if (score.isNew) {
      return this.scorecardService.createScore({
        ...score,
        plusMinus,
      });
    } else {
      return this.scorecardService.updateScore({
        ...score,
        plusMinus,
      });
    }
  }

  private formatPlusMinus(plusMinus: number | undefined): string {
    return this.calculationService.formatPlusMinus(plusMinus);
  }

  getTabIndex(rowId: string, holeNumber: number): number {
    return this.utilitiesService.calculateTabIndex(
      this.scorecardMode,
      this.scoringType,
      rowId,
      holeNumber,
      this.scorecards,
      SCORECARD_CONSTANTS.HOLES_PER_ROUND
    );
  }

  numberOnly(event: KeyboardEvent): boolean {
    const isNumericInput = this.utilitiesService.isNumericInput(event);

    // Prevent '0' key from being entered only if it would be the sole character
    // (i.e., prevent "0" but allow "10", "20", etc.)
    if (event.key === '0') {
      const input = event.target as HTMLInputElement;
      const currentValue = input.value;
      const selectionStart = input.selectionStart || 0;
      const selectionEnd = input.selectionEnd || 0;

      // Calculate what the value would be after this keypress
      const newValue =
        currentValue.substring(0, selectionStart) +
        '0' +
        currentValue.substring(selectionEnd);

      // Prevent if the result would be just "0"
      if (newValue === '0') {
        return false;
      }
    }

    return isNumericInput;
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitizedValue = this.utilitiesService.sanitizeNumericInput(
      input.value
    );

    if (input.value !== sanitizedValue) {
      input.value = sanitizedValue;
      input.dispatchEvent(new Event('input'));
    }
  }

  onRequestFocusAdvancement(event: Event): void {
    const customEvent = event as CustomEvent;
    const { currentHoleNumber, currentPlayerId } = customEvent.detail;

    if (currentHoleNumber) {
      // Set up the active score input to help the focus management system
      this.activeScoreInput = {
        rowId: currentPlayerId,
        holeNumber: currentHoleNumber,
      };

      // Call the existing focus management with the next hole
      this.setNextHoleFocus(currentHoleNumber + 1);
    }
  }
  onYNInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitizedValue = this.utilitiesService.sanitizeYesNoInput(
      input.value
    );

    if (input.value !== sanitizedValue) {
      input.value = sanitizedValue;
      input.dispatchEvent(new Event('input'));
    } else {
      // If the value didn't change after sanitization, but it's a valid Y/N input,
      // we still need to trigger focus advancement for the AutoFocusNextDirective
      if (
        sanitizedValue &&
        (sanitizedValue.toUpperCase() === 'Y' ||
          sanitizedValue.toUpperCase() === 'N')
      ) {
        // Trigger a custom event that the directive can listen for
        input.dispatchEvent(
          new CustomEvent('scoreProcessed', {
            detail: { value: sanitizedValue },
            bubbles: true,
          })
        );
      }
    }
  }

  onVerifyScoreChange(event: Event, index: number, type: 'IN' | 'OUT'): void {
    const value = (event.target as HTMLInputElement).value;
    const numericValue = value ? parseInt(value) : null;

    // Validate that the score is not 0 (invalid golf score)
    if (
      numericValue !== null &&
      !this.utilitiesService.isValidScore(numericValue)
    ) {
      this.loggingService.warn(
        'ScoreCard',
        `Invalid verify score attempted: ${numericValue}. Ignoring input.`
      );
      // Clear the input if invalid
      (event.target as HTMLInputElement).value = '';
      return;
    }

    if (type === 'OUT') {
      this.verifyScoresOUT[index].score = numericValue;
    } else {
      this.verifyScoresIN[index].score = numericValue;
    }
    this.setVerifyValid();
  }

  private verifyScoreSet(
    scores: ScoreResponse[],
    verifyScores: VerifyScore[]
  ): boolean {
    const result = this.calculationService.validateVerifyScoreSet(
      scores,
      verifyScores
    );

    // Update the original verifyScores array with validation results
    verifyScores.forEach((verifyScore, index) => {
      if (result.updatedVerifyScores[index]) {
        verifyScore.valid = result.updatedVerifyScores[index].valid;
      }
    });

    return result.isValid;
  }

  setVerifyValid(): void {
    if (!this.isVerifyMode()) return;

    const scorecardId = this.getFilteredScorecard()?.scorecardId!;

    const validOUT = this.verifyScoreSet(
      this.scoresOUT[scorecardId],
      this.verifyScoresOUT
    );

    const validIN = this.verifyScoreSet(
      this.scoresIN[scorecardId],
      this.verifyScoresIN
    );

    this.validOUT = validOUT;
    this.validIN = validIN;
  }

  markOfficial(): void {
    const scorecard = this.filteredScorecards[0];

    // Track this scorecard ID as a local operation to prevent showing notifications (only if suppression is enabled)
    const suppressLocalOperations =
      this.configService.config?.notifications?.suppressLocalOperations ??
      false;
    if (suppressLocalOperations) {
      const timestamps =
        this.localScoreOperations.get(scorecard.scorecardId!) || [];
      timestamps.push(Date.now());

      // Keep only the last 10 operations per scorecard to prevent memory issues
      if (timestamps.length > 10) {
        timestamps.splice(0, timestamps.length - 10);
      }

      this.localScoreOperations.set(scorecard.scorecardId!, timestamps);

      // IMPORTANT: Also register with SignalR service for consistent suppression
      this.scoringRealtimeService.registerLocalScorecard(
        scorecard.scorecardId!
      );
    }

    const $markOperation = scorecard.official
      ? this.scorecardService.markNotOfficial(scorecard.scorecardId!)
      : this.scorecardService.markOfficial(scorecard.scorecardId!);

    $markOperation.subscribe({
      next: (response) => {
        this.scorecards = this.mapScorecardsToDisplay(response);
        this.refreshRoundScorecardCounts();

        this.toasterService.success(
          response.official
            ? `Scorecard marked Official for ${response.playerLastName}, ${response.playerFirstName}`
            : `Scorecard marked Unofficial for ${response.playerLastName}, ${response.playerFirstName}`
        );
      },
      error: (error) => {
        // Remove from local operations set if operation fails
        this.localScoreOperations.delete(scorecard.scorecardId!);
        this.loggingService.error(
          'ScoreCard',
          'Error marking scorecard:',
          error
        );
      },
    });
  }

  getVerifyScoresTotal(type: VerifyScoreType): number | string {
    return this.utilitiesService.calculateVerifyScoresTotal(
      type,
      this.verifyScoresOUT,
      this.verifyScoresIN,
      this.getFilteredScorecard()?.startScore
    );
  }

  cancelScoreEdits(): void {
    this.fetchScores().subscribe();
    this.verifyScoreMode = this.INITIAL_VERIFY_MODE;
    this.setVerifyValid();
  }

  editVerifyScores(): void {
    this.verifyScoreMode = 'edit';

    const scorecard = this.getFilteredScorecard();

    this.setFirstInputFocus();

    this.toasterService.info(
      `Editing Scores for ${scorecard?.playerLastName}, ${scorecard?.playerFirstName}`
    );
  }

  private findFirstEmptyInput(
    inputs: ElementRef[]
  ): ElementRef<HTMLInputElement> | undefined {
    return this.utilitiesService.findFirstEmptyInput(inputs);
  }

  private findFirstInvalidInput(
    inputs: ElementRef[]
  ): ElementRef<HTMLInputElement> | undefined {
    return this.utilitiesService.findFirstInvalidInput(
      inputs,
      this.verifyScoresOUT,
      this.verifyScoresIN
    );
  }

  /**
   * Finds the first available blank score input considering the group's start tee
   * @param inputs Array of input element references
   * @param rowId The row ID (player ID) to filter inputs for
   * @returns The first available input element or undefined
   */
  private findFirstAvailableInput(
    inputs: ElementRef[],
    rowId: string
  ): ElementRef<HTMLInputElement> | undefined {
    if (!this.isScoresMode()) {
      return this.findFirstEmptyInput(inputs);
    }

    const selectedGroup = this.selectedGroupSignal();
    if (!selectedGroup?.startTee) {
      return this.findFirstEmptyInput(inputs);
    }

    const startTee = selectedGroup.startTee;

    // Get all inputs for the specific player (rowId)
    const playerInputs = inputs.filter((input) => {
      const element = input.nativeElement as HTMLInputElement;
      return element.getAttribute('data-row-id') === rowId;
    });

    if (playerInputs.length === 0) {
      return this.findFirstEmptyInput(inputs);
    }

    // Create a map of hole number to input element for easier lookup
    const holeInputMap = new Map<number, ElementRef<HTMLInputElement>>();
    playerInputs.forEach((input) => {
      const element = input.nativeElement as HTMLInputElement;
      const holeNumber = parseInt(
        element.getAttribute('data-hole-number') || '0'
      );
      if (holeNumber > 0) {
        holeInputMap.set(holeNumber, input);
      }
    });

    // Create an ordered list of holes starting from the start tee
    const orderedHoles: number[] = [];

    // Add holes from start tee to the last hole using the constant
    for (
      let hole = startTee;
      hole <= SCORECARD_CONSTANTS.HOLES_PER_ROUND;
      hole++
    ) {
      orderedHoles.push(hole);
    }

    // If start tee is not 1, add holes from 1 to start tee - 1
    if (startTee > 1) {
      for (let hole = 1; hole < startTee; hole++) {
        orderedHoles.push(hole);
      }
    }

    // Find the first empty input following the hole order
    for (const holeNumber of orderedHoles) {
      const input = holeInputMap.get(holeNumber);
      if (input && this.isInputEmpty(input.nativeElement)) {
        return input;
      }
    }

    // If no empty input found, return the first available input as fallback
    return this.findFirstEmptyInput(inputs);
  }

  /**
   * Finds the first enabled input in stats mode, prioritizing score inputs first
   * @param inputs Array of input element references
   * @param rowId The row ID (player ID) to filter inputs for
   * @returns The first enabled input element or undefined
   */
  private findFirstEnabledStatsInput(
    inputs: ElementRef[],
    rowId: string
  ): ElementRef<HTMLInputElement> | undefined {
    const selectedGroup = this.selectedGroupSignal();
    const startTee = selectedGroup?.startTee || 1;

    // Define row priority order (score inputs first, then stats)
    const rowPriorities = [
      rowId,
      'fairway',
      'gir',
      'sandSaveMade',
      'putts',
      'driveDistance',
    ];

    // Create an ordered list of holes starting from the start tee
    const orderedHoles: number[] = [];
    for (
      let hole = startTee;
      hole <= SCORECARD_CONSTANTS.HOLES_PER_ROUND;
      hole++
    ) {
      orderedHoles.push(hole);
    }
    if (startTee > 1) {
      for (let hole = 1; hole < startTee; hole++) {
        orderedHoles.push(hole);
      }
    }

    // Try to find the first enabled input by priority (row type) and then by hole order
    for (const priority of rowPriorities) {
      for (const holeNumber of orderedHoles) {
        const input = inputs.find((input) => {
          const element = input.nativeElement as HTMLInputElement;
          const inputRowId = element.getAttribute('data-row-id');
          const inputHoleNumber = parseInt(
            element.getAttribute('data-hole-number') || '0'
          );
          return inputRowId === priority && inputHoleNumber === holeNumber;
        });

        if (input && !this.isInputDisabled(input.nativeElement)) {
          return input;
        }
      }
    }

    // Fallback to any enabled input
    const enabledInput = inputs.find(
      (input) => !this.isInputDisabled(input.nativeElement)
    );
    return enabledInput;
  }

  /**
   * Checks if an input element is empty or contains only whitespace
   * @param input The input element to check
   * @returns True if the input is empty, false otherwise
   */
  private isInputEmpty(input: HTMLInputElement): boolean {
    return !input.value || input.value.trim() === '';
  }

  /**
   * Check if an input element is currently focused
   */
  private isInputCurrentlyFocused(): boolean {
    const activeElement = document.activeElement;
    return (
      activeElement?.tagName === 'INPUT' ||
      activeElement?.tagName === 'TEXTAREA'
    );
  }

  private setFirstInputFocus(): void {
    // Ensure DOM is ready
    requestAnimationFrame(() => {
      const inputs = this.scoreInputs.toArray();

      let firstInput: ElementRef<HTMLInputElement> | undefined;

      if (this.isVerifyMode()) {
        firstInput = this.findFirstInvalidInput(inputs);

        // If no invalid input found, try to find the first verify input
        if (!firstInput) {
          firstInput = inputs.find((input) => {
            const element = input.nativeElement as HTMLInputElement;
            return (
              element instanceof HTMLInputElement &&
              element.getAttribute('data-row-id') === 'verify' &&
              !element.disabled &&
              element.offsetParent !== null &&
              element.getBoundingClientRect().height > 0
            );
          });
        }
      } else {
        const rowId = this.isScoresMode()
          ? this.scorecards[0]?.playerId
          : this.selectedPlayer?.playerId;

        // Guard against missing rowId for non-verify modes
        if (!rowId) return;

        if (this.isStatsMode()) {
          // In stats mode, find the first enabled input prioritizing score inputs
          firstInput = this.findFirstEnabledStatsInput(inputs, rowId);
        } else {
          firstInput = this.findFirstAvailableInput(inputs, rowId);
        }
      }

      if (firstInput?.nativeElement) {
        firstInput.nativeElement.focus();
        firstInput.nativeElement.select();

        // Add a temporary visual indicator to make focus more obvious
        const element = firstInput.nativeElement;
        const originalBorder = element.style.border;
        const originalBoxShadow = element.style.boxShadow;

        element.style.border = '2px solid #007bff';
        element.style.boxShadow = '0 0 5px rgba(0, 123, 255, 0.5)';

        // Remove the visual indicator after a short time
        setTimeout(() => {
          element.style.border = originalBorder;
          element.style.boxShadow = originalBoxShadow;
        }, 1500);
      }
    });
  }

  isOfficial(): boolean {
    return this.getFilteredScorecard()?.official || false;
  }

  isVerified(): boolean {
    return this.getFilteredScorecard()?.verified || false;
  }

  isVerifyMode(): boolean {
    return this.scorecardMode === 'verify';
  }

  isScoresMode(): boolean {
    return this.scorecardMode === 'scores';
  }

  isStatsMode(): boolean {
    return this.scorecardMode === 'stats';
  }

  isScoreInputVisible(score: ScoreResponse): boolean {
    return this.utilitiesService.isScoreInputVisible(
      score,
      this.showAllScores,
      this.verifyScoresOUT,
      this.verifyScoresIN
    );
  }

  private areScoresComplete(scores: VerifyScore[]): boolean {
    return this.calculationService.areScoresComplete(scores);
  }

  isScoreTotalVisible(direction: 'in' | 'out' | 'rnd' | 'tot'): boolean {
    return this.utilitiesService.isScoreTotalVisible(
      direction,
      this.showAllScores,
      this.verifyScoresOUT,
      this.verifyScoresIN
    );
  }

  isVerifyVisible(): boolean {
    return (
      this.configService.config?.showVerifiedButton! && this.isVerifyMode()
    );
  }

  isDebugPanelVisible(): boolean {
    return this.configService.config?.scoring?.showDebugPanelButton ?? false;
  }

  isPlayerScorecardOfficial(playerId: string): boolean {
    const scorecard = this.scorecards.find((sc) => sc.playerId === playerId);
    return scorecard?.official || false;
  }

  /**
   * Refreshes scorecard counts for the current round asynchronously
   */
  private refreshRoundScorecardCounts(): void {
    const round = this.selectedRound();
    if (round) {
      setTimeout(() => {
        const rid = round.roundId;
        this.scorecardService.getScorecardCounts(rid!).subscribe({
          next: () => {
            this.loggingService.debug(
              'ScoreCard',
              'Scorecard counts refreshed successfully',
              this.officialScorecardsCount()
            );
          },
          error: (error) => {
            this.loggingService.error(
              'ScoreCard',
              'Error refreshing scorecard counts:',
              error
            );
          },
        });
      }, 0);
    }
  }

  canMarkOfficial(): boolean {
    if (!this.isVerifyMode()) return false;

    const scorecard = this.getFilteredScorecard();
    if (!scorecard) return false;

    return this.calculationService.canMarkOfficialValidation(
      this.scoresOUT[scorecard.scorecardId!] || [],
      this.scoresIN[scorecard.scorecardId!] || [],
      this.verifyScoresOUT || [],
      this.verifyScoresIN || [],
      this.validOUT,
      this.validIN,
      this.dirtyScoreCount()
    );
  }

  openShotModal(scorecard: ScorecardResponse, score: ScoreResponse): void {
    const hole = this.findScoreHole(score);

    const params = {
      playerName: `${scorecard.playerFirstName} ${scorecard.playerLastName}`,
      par: hole?.par!,
      score,
    } as ShotListModalParams;

    if (score.isNew || score.isDirty) {
      this.shotListModal.openModal(params);
    } else {
      this.scorecardService
        .getScoreById(score!.scorecardId!, score.scoreId!)
        .subscribe((s: ScoreResponse) => {
          const params = {
            playerName: `${scorecard.playerFirstName} ${scorecard.playerLastName}`,
            par: hole?.par!,
            score: s,
          } as ShotListModalParams;

          this.shotListModal.openModal(params);
        });
    }
  }

  /**
   * Updates display properties for a score object to reflect current stats values
   * @param score The score object to update
   */
  private updateScoreDisplayProperties(score: ScoreResponse): void {
    const hasFairway = this.hasFairway(score.holeId || '');
    const isDriveMeasuredForHole = this.isDriveMeasured(score.holeId || '');

    score.fairwayDisplay = hasFairway ? (score.fairway ? 'Y' : 'N') : '';
    score.girDisplay = score.gir ? 'Y' : 'N';
    score.sandSaveMadeDisplay = score.sandSaveAttempted
      ? score.sandSaveMade
        ? 'Y'
        : 'N'
      : '';
    // Show drive distance if it exists, regardless of isDriveMeasured flag
    // The flag controls editability, not visibility of existing data
    score.driveDistanceDisplay =
      score.driveDistance && score.driveDistance > 0
        ? Number(score.driveDistance)
        : isDriveMeasuredForHole
        ? 0
        : '';
  }

  /**
   * Updates dirty flags for all stats based on comparison with original values
   * @param score The score object to update dirty flags for
   */
  private updateStatsDirtyFlags(score: ScoreResponse): void {
    score.isFairwayDirty = score.originalFairway! !== score.fairway;
    score.isGirDirty = score.originalGir! !== score.gir!;
    score.isPuttsDirty = +score.originalPutts! !== +score.putts!;
    score.isSandSaveDirty =
      score.originalSandSaveMade! !== score.sandSaveMade &&
      score.originalSandSaveAttempted! !== score.sandSaveAttempted;
  }

  onShotsUpdated(params: ShotListModalParams): void {
    const scores = this.scores[params.score.scorecardId!];

    const existingScore = scores?.find(
      (s) => s.scoreId === params.score.scoreId
    );

    if (!existingScore) {
      this.loggingService.warn(
        'ScoreCard',
        'Score not found for update:',
        params.score
      );
      return;
    }

    Object.assign(existingScore, params.score);

    existingScore.shots = params.score.shots;

    // Update display properties and dirty flags to reflect the new stats values
    this.updateScoreDisplayProperties(existingScore);
    this.updateStatsDirtyFlags(existingScore);

    if (!this.isScoresMode()) {
      const originalShots = existingScore.originalShots || [];
      const currentShots = existingScore.shots || [];

      const shotsChanged =
        originalShots.length !== currentShots.length ||
        !originalShots.every((shot) =>
          currentShots.some((s) => s.shotId === shot.shotId)
        );

      if (shotsChanged) {
        existingScore.isDirty = true;
      }
    }

    this.setStat(existingScore, 'score', existingScore.holeNumber!);
    this.setScore(existingScore, 'score', existingScore.holeNumber!);
  }

  openPlayerSearchModal(): void {
    this.playerSearchModal.openModal();
  }

  // Add missing mapScorecardToDisplay method
  private mapScorecardToDisplay(
    scorecard: ScorecardResponse
  ): ScorecardResponse {
    return this.utilitiesService.mapScorecardToDisplayFormat(scorecard);
  }

  // Handler for toggling the hideOfficialScorecards flag from the UI (e.g., checkbox)
  handleHideOfficialCheck(): void {
    this.initializeData();
  }

  /**
   * Debug method to clear group service cache and reload data
   * This can help diagnose issues with stale or missing group data
   */
  reloadGroupData(): void {
    this.loggingService.debug(
      'ScoreCard',
      'Manually forcing group data reload'
    );

    // Access the private fields of the group service using type assertion
    const groupServiceAny = this.groupService as any;

    // Clear cached groups and reset ongoing requests
    groupServiceAny.groups = [];
    groupServiceAny.ongoingGroupsRequest = null;

    // Force reload of the data

    this.initializeData();
  }

  // Helper method for template to check if a player is selected
  isPlayerSelected(player: Player): boolean {
    return player.playerId === this.selectedPlayerSignal()?.playerId;
  }

  // Existing helper method for group selection
  isGroupSelected(group: Group): boolean {
    const selectedGroup = this.selectedGroupSignal();
    return selectedGroup?.groupId === group.groupId;
  }

  /**
   * Check if a score is currently being synchronized
   */
  isScoreSyncing(score: ScoreResponse): boolean {
    const key = `${score.scorecardId}-${score.holeNumber}`;
    return this.scoreBufferService.isScoreSyncing(key);
  }

  /**
   * Get the status of a specific score in the buffer
   */
  getScoreBufferStatus(score: ScoreResponse): string | null {
    const key = `${score.scorecardId}-${score.holeNumber}`;
    return this.scoreBufferService.getScoreStatus(key);
  }

  /**
   * Called when navigating away from the component
   */
  ngOnDestroy() {
    // Flush any pending changes before leaving
    this.scoreBufferService.flushBuffer().subscribe();

    // Existing cleanup code would go here if there was any
  }

  /**
   * Debug method to force buffer processing (for testing)
   */
  forceBufferProcessing(): void {
    this.loggingService.debug('ScoreCard', 'Force processing buffer...');
    this.scoreBufferService.forceProcessing();
  }

  /**
   * Debug method to check offline status
   */
  getOfflineStatus(): void {
    const status = this.scoreBufferService.getOfflineStatus();
    this.loggingService.debug('ScoreCard', 'Current offline status:', status);
  }

  /**
   * Debug method to force sync offline scores
   */
  forceSyncOfflineScores(): void {
    this.loggingService.debug('ScoreCard', 'Force sync offline scores...');
    this.scoreBufferService.forceSyncOfflineScores();
  }

  /**
   * Debug method to simulate offline mode
   */
  simulateOffline(): void {
    this.loggingService.debug('ScoreCard', 'Simulating offline mode...');
    // Temporarily override navigator.onLine
    Object.defineProperty(navigator, 'onLine', {
      value: false,
      configurable: true,
    });
    // Trigger offline event
    window.dispatchEvent(new Event('offline'));
  }

  /**
   * Debug method to simulate online mode
   */
  simulateOnline(): void {
    this.loggingService.debug('ScoreCard', 'Simulating online mode...');
    // Restore navigator.onLine
    Object.defineProperty(navigator, 'onLine', {
      value: true,
      configurable: true,
    });
    // Trigger online event
    window.dispatchEvent(new Event('online'));
  }

  // =========================================================================
  // SIGNALR PLAYER SUBSCRIPTION MANAGEMENT
  // =========================================================================

  /**
   * Subscribe to SignalR events for all players in the selected group
   * This ensures the Admin app receives real-time scorecard updates for the currently displayed group
   */
  private async subscribeToSelectedGroupPlayers(group: Group): Promise<void> {
    try {
      this.loggingService.info(
        'ScoreCard',
        `Subscribing to SignalR events for group ${group.number} players`
      );

      // Pass the scorecards to the service so it can extract player IDs correctly
      await this.scoringRealtimeService.subscribeToGroupPlayers(
        group,
        this.scorecards
      );

      this.loggingService.info(
        'ScoreCard',
        `Successfully subscribed to group ${group.number} player events`
      );
    } catch (error) {
      this.loggingService.error(
        'ScoreCard',
        `Failed to subscribe to group ${group.number} player events:`,
        error
      );
    }
  }

  /**
   * Unsubscribe from SignalR events for players (called when switching groups or leaving component)
   */
  private async unsubscribeFromCurrentGroupPlayers(): Promise<void> {
    if (!this.selectedGroup) {
      return;
    }

    try {
      this.loggingService.info(
        'ScoreCard',
        `Unsubscribing from SignalR events for group ${this.selectedGroup.number} players`
      );

      // Extract player IDs to unsubscribe from using scorecards
      const playerIds: string[] = [];

      if (this.scorecards && Array.isArray(this.scorecards)) {
        for (const scorecard of this.scorecards) {
          if (scorecard.playerId) {
            playerIds.push(scorecard.playerId);
          }
        }
      }

      if (playerIds.length > 0) {
        // Remove duplicates
        const uniquePlayerIds = [...new Set(playerIds)];
        await this.scoringRealtimeService.unsubscribeFromPlayers(
          uniquePlayerIds
        );
        this.loggingService.info(
          'ScoreCard',
          `Successfully unsubscribed from ${uniquePlayerIds.length} player events`
        );
      }
    } catch (error) {
      this.loggingService.error(
        'ScoreCard',
        `Failed to unsubscribe from current group player events:`,
        error
      );
    }
  }
}
